import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { recipes as recipeApi, labels as labelsApi, buzz as buzzApi, ai, shared, hives as hivesApi } from '../utils/api';
import { Edit, Camera, ChevronDown, ChevronUp, MessageSquare, Trash2, RotateCcw, ChefHat, Tag, X, Plus, Timer, Printer, Send, Loader, ExternalLink, List, Check } from 'lucide-react';
import StarRating from '../components/StarRating';
import DraggableChat from '../components/DraggableChat';
import EditRecipeModal from '../components/EditRecipeModal';
import RecipePreview from '../components/RecipePreview';
import RecipePlaceholder from '../components/RecipePlaceholder';
import BottomNav from '../components/BottomNav';
import PrintModal from '../components/PrintModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { SHOW_HELP_TILES_EVENT } from '../components/HowHoneyWorks';
import { trackCurrentlyCookingAdded, trackCurrentlyCookingRemoved } from '../utils/analytics';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';

// Error Boundary Component
class RecipeDetailErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RecipeDetail] Error Boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <div className="text-6xl mb-4">😵</div>
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">Error: {this.state.error?.message}</p>
            <button 
              onClick={() => window.location.href = '/recipe-box'}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg"
            >
              Go back to Recipe Box
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Words to skip when extracting ingredient match words
const INGREDIENT_SKIP_WORDS = new Set(['large', 'small', 'medium', 'fresh', 'dried', 'ground', 'chopped', 'minced', 'sliced', 'diced', 'whole', 'hot', 'cold', 'warm', 'raw', 'cooked', 'baking', 'cooking', 'mixing', 'salad', 'prepared', 'unsalted', 'low-sodium', 'frozen', 'crumbled', 'baby', 'kosher', 'cloves', 'leaves', 'and', 'the', 'for', 'with', 'your', 'you', 'did']);

// Timer launch helper - detects platform and launches native timer
// Currently only Windows has reliable native timer URL scheme from web
const canLaunchNativeTimer = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Android detection - Google Clock intent
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  // Windows detection - ms-clock: protocol
  if (navigator.platform && navigator.platform.indexOf('Win') !== -1) {
    return 'windows';
  }
  
  // iOS and macOS - no reliable native timer URL scheme from web browsers
  return null;
};

const launchNativeTimer = (minutes, label) => {
  const platform = canLaunchNativeTimer();
  const seconds = minutes * 60;
  const truncatedLabel = label.length > 50 ? label.substring(0, 47) + '...' : label;
  
  try {
    switch (platform) {
      case 'android':
        // Android Google Clock intent
        window.location.href = `intent://timer?seconds=${seconds}&message=${encodeURIComponent(truncatedLabel)}#Intent;scheme=android-app;package=com.google.android.deskclock;end`;
        break;
        
      case 'windows':
        // Windows Alarms & Clock app
        window.location.href = `ms-clock:timer?minutes=${minutes}&label=${encodeURIComponent(truncatedLabel)}`;
        break;
        
      default:
        return false;
    }
    return true;
  } catch (e) {
    console.error('Failed to launch timer:', e);
    return false;
  }
};

// Helper function to extract matching words from ingredient name
const getIngredientWords = (ingredientName) => {
  // Remove parenthetical content like "(cannellini or great northern)"
  const cleanName = ingredientName.replace(/\s*\([^)]*\)/g, '').toLowerCase().trim();
  // Split and filter
  const words = cleanName.split(/\s+/).filter(w => w.length >= 3 && !INGREDIENT_SKIP_WORDS.has(w));
  return { cleanName, words };
};

// Helper function to highlight ingredient names in instruction text
const highlightIngredients = (text, ingredients) => {
  if (!text) return text;
  
  // First, handle markdown-style **bold** text (convert to orange bold)
  // This handles cases like "**add a photo**"
  const markdownBoldRegex = /\*\*([^*]+)\*\*/g;
  let processedText = text;
  const markdownMatches = [];
  let match;
  
  while ((match = markdownBoldRegex.exec(text)) !== null) {
    markdownMatches.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[1], // The text inside the **
      isMarkdown: true
    });
  }
  
  // If there are markdown matches but no ingredients, just render markdown
  if (markdownMatches.length > 0 && (!ingredients || ingredients.length === 0)) {
    const parts = [];
    let lastIndex = 0;
    markdownMatches.forEach((r, idx) => {
      if (r.start > lastIndex) {
        parts.push(text.substring(lastIndex, r.start));
      }
      parts.push(<strong key={idx} className="text-amber-700">{r.text}</strong>);
      lastIndex = r.end;
    });
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return <>{parts}</>;
  }
  
  if (!ingredients || ingredients.length === 0) {
    return text;
  }

  const replacements = [...markdownMatches]; // Start with markdown matches
  const textLower = text.toLowerCase();

  // Process each ingredient
  ingredients.forEach(ing => {
    if (!ing.name) return;
    
    const { cleanName, words } = getIngredientWords(ing.name);
    
    // Try full clean name match first
    if (cleanName.length > 2) {
      const regex = new RegExp(`\\b(${cleanName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        // Skip if this overlaps with a markdown match
        const overlapsMarkdown = markdownMatches.some(m => 
          (match.index >= m.start && match.index < m.end) ||
          (match.index + match[0].length > m.start && match.index + match[0].length <= m.end)
        );
        if (!overlapsMarkdown) {
          replacements.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0]
          });
        }
      }
    }
    
    // Try individual significant words if no full match
    if (!textLower.includes(cleanName)) {
      for (const word of words) {
        // Escape special regex characters in the word
        const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b(${escapedWord}s?)\\b`, 'gi');
        let match;
        while ((match = regex.exec(text)) !== null) {
          // Skip if this overlaps with a markdown match
          const overlapsMarkdown = markdownMatches.some(m => 
            (match.index >= m.start && match.index < m.end) ||
            (match.index + match[0].length > m.start && match.index + match[0].length <= m.end)
          );
          if (!overlapsMarkdown) {
            replacements.push({
              start: match.index,
              end: match.index + match[0].length,
              text: match[0]
            });
          }
        }
      }
    }
  });

  // If no matches, return original text
  if (replacements.length === 0) {
    return text;
  }

  // Sort by start position and remove overlaps
  replacements.sort((a, b) => a.start - b.start);
  const filtered = [];
  let lastEnd = -1;
  for (const r of replacements) {
    if (r.start >= lastEnd) {
      filtered.push(r);
      lastEnd = r.end;
    }
  }

  // Build result with React elements
  const parts = [];
  let lastIndex = 0;
  filtered.forEach((r, idx) => {
    if (r.start > lastIndex) {
      parts.push(text.substring(lastIndex, r.start));
    }
    parts.push(<strong key={idx} className="text-amber-700">{r.text}</strong>);
    lastIndex = r.end;
  });
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <>{parts}</>;
};

const RecipeDetail = () => {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if recipe was passed via navigation state (instant load from Currently Cooking)
  const passedRecipe = location.state?.recipe;
  
  // Use passedRecipe for instant display, but always fetch full data for instructions
  const [recipe, setRecipe] = useState(passedRecipe || null);
  const [loading, setLoading] = useState(!passedRecipe);
  const [fullDataLoaded, setFullDataLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('ingredients'); // ingredients, instructions
  // Checked ingredients - persisted in localStorage while recipe is in "Currently Cooking"
  const [checkedIngredients, setCheckedIngredients] = useState(() => {
    try {
      const saved = localStorage.getItem(`cookingChecks_${recipeId}`);
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (e) {
      // Silent fail
    }
    return new Set();
  });
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [rating, setRating] = useState(0);
  const [cooked, setCooked] = useState(false);
  const [currentMeal, setCurrentMeal] = useState(false);
  const [upNext, setUpNext] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUpdatePreview, setShowUpdatePreview] = useState(false);
  const fileInputRef = React.useRef(null);
  const [updatePreviewRecipe, setUpdatePreviewRecipe] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Recipe scaling factor: 0.5, 1, or 2 - persisted per recipe in localStorage
  const [scale, setScale] = useState(() => {
    try {
      const savedScale = localStorage.getItem(`recipeScale_${recipeId}`);
      return savedScale ? parseFloat(savedScale) : 1;
    } catch {
      return 1;
    }
  });
  const [showPrintModal, setShowPrintModal] = useState(false); // Print options modal
  const [sharing, setSharing] = useState(false); // Share in progress
  const [computedTweaks, setComputedTweaks] = useState([]); // Computed tweaks from source
  const [buzzCount, setBuzzCount] = useState(0); // Count of buzzes for this recipe
  const [cookCount, setCookCount] = useState(0); // Count of users who have this recipe
  const [headerHeight, setHeaderHeight] = useState(80); // Dynamic header height for sticky tabs
  const headerRef = React.useRef(null);

  // Persist scale to localStorage when it changes
  useEffect(() => {
    if (recipeId) {
      localStorage.setItem(`recipeScale_${recipeId}`, scale.toString());
    }
  }, [scale, recipeId]);

  // Persist checked ingredients to localStorage while recipe is in "Currently Cooking"
  useEffect(() => {
    if (recipeId && currentMeal && checkedIngredients.size > 0) {
      // Save checkmarks while cooking
      localStorage.setItem(`cookingChecks_${recipeId}`, JSON.stringify([...checkedIngredients]));
    }
  }, [checkedIngredients, currentMeal, recipeId]);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = React.useRef(true);
  const fetchInProgressRef = React.useRef(false); // Prevent duplicate fetches
  useEffect(() => {
    isMountedRef.current = true;
    return () => { 
      isMountedRef.current = false; 
    };
  }, [recipeId]);
  
  // Mobile layout state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const mainContentRef = React.useRef(null);
  const tabBarRef = React.useRef(null);
  
  // Calculate header height for sticky tab positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [recipe]); // Recalculate when recipe loads (title may affect height)
  
  // Track scroll position per tab (within-page only, not persisted)
  // Initialize with 0 so scroll tracking starts immediately
  const scrollPositions = React.useRef({ ingredients: 0, instructions: 0 });
  // Track which tabs have been visited while sticky (for first-visit scroll behavior)
  const visitedWhileSticky = React.useRef({ ingredients: false, instructions: false });
  // Store the scroll position where sticky mode begins (captured once)
  const stickyThresholdScroll = React.useRef(null);
  
  // Check if tabs are currently in sticky position
  const areTabsSticky = () => {
    if (!tabBarRef.current) return false;
    const rect = tabBarRef.current.getBoundingClientRect();
    // Tab bar is sticky when its top is at or below the header bottom
    return rect.top <= headerHeight + 1;
  };
  
  // Track scroll position for active tab while scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (!tabBarRef.current) return;
      
      const rect = tabBarRef.current.getBoundingClientRect();
      const isSticky = rect.top <= headerHeight + 1;
      
      // Capture the threshold scroll position when we first enter sticky mode
      if (isSticky && stickyThresholdScroll.current === null) {
        // We just entered sticky mode - current scrollY is at the threshold
        // Add a small buffer to ensure we stay solidly IN sticky territory when scrolling here
        stickyThresholdScroll.current = window.scrollY + 5;
      }
      
      if (isSticky) {
        // Always track scroll position when sticky
        scrollPositions.current[activeTab] = window.scrollY;
        // Mark this tab as visited while sticky
        visitedWhileSticky.current[activeTab] = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab, headerHeight]);
  
  // Handle tab switch with scroll position memory
  const handleTabSwitch = (newTab) => {
    if (newTab === activeTab) return;
    
    const tabsSticky = areTabsSticky();
    
    // Save current scroll position before switching (if sticky)
    if (tabsSticky) {
      scrollPositions.current[activeTab] = window.scrollY;
      visitedWhileSticky.current[activeTab] = true;
    }
    
    setActiveTab(newTab);
    
    // Only adjust scroll when tabs are sticky
    if (tabsSticky) {
      const hasVisitedNewTab = visitedWhileSticky.current[newTab];
      
      if (hasVisitedNewTab) {
        // Returning to a previously visited tab - restore saved position
        const savedPosition = scrollPositions.current[newTab];
        requestAnimationFrame(() => {
          window.scrollTo(0, savedPosition);
        });
      } else {
        // First visit to this tab while sticky
        // Scroll to the threshold position (top of content, just below sticky headers)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Use captured threshold, or calculate fallback
            const targetScroll = stickyThresholdScroll.current !== null 
              ? stickyThresholdScroll.current 
              : Math.max(0, (tabBarRef.current?.offsetTop || 0) - headerHeight);
            
            window.scrollTo(0, targetScroll);
            scrollPositions.current[newTab] = targetScroll;
            visitedWhileSticky.current[newTab] = true;
          });
        });
      }
    }
    // When not sticky, don't change scroll at all
  };
  
  // Check if we came from the chat flow (for showing "Back to Chat" button)
  const [cameFromChat, setCameFromChat] = useState(false);
  const [cameFromBuzz, setCameFromBuzz] = useState(false);
  const [cameFromBrowse, setCameFromBrowse] = useState(false);
  const [cameFromFeed, setCameFromFeed] = useState(false);
  const [cameFromHive, setCameFromHive] = useState(false);
  const [fromHiveId, setFromHiveId] = useState(null);
  const [fromHiveName, setFromHiveName] = useState(null);
  const [feedHiveFilter, setFeedHiveFilter] = useState(null);
  const [showCookAlongTip, setShowCookAlongTip] = useState(false);
  
  // Feature tips
  const [showCurrentlyMealTip, setShowCurrentlyMealTip] = useState(false);
  const [showPhotoTip, setShowPhotoTip] = useState(false);
  
  // Share to Buzz state
  const [showBuzzModal, setShowBuzzModal] = useState(false);
  const [buzzCaption, setBuzzCaption] = useState('');
  const [buzzPhoto, setBuzzPhoto] = useState(null);
  const [buzzRating, setBuzzRating] = useState(0);
  const [sharingToBuzz, setSharingToBuzz] = useState(false);
  const [myHives, setMyHives] = useState([]);
  const [selectedHives, setSelectedHives] = useState([]);
  const [showInMyBuzz, setShowInMyBuzz] = useState(true);
  const [loadingHives, setLoadingHives] = useState(false);
  
  // Buzz source editing state
  const [buzzSourceType, setBuzzSourceType] = useState('url');
  const [buzzSourceUrl, setBuzzSourceUrl] = useState('');
  const [buzzBookSource, setBuzzBookSource] = useState({ title: '', author: '', page: '' });
  const [showBuzzSourceEdit, setShowBuzzSourceEdit] = useState(false);
  
  // Average rating from buzzes
  const [averageRating, setAverageRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  
  // Custom source state (editable source for recipes without external source)
  const [customSource, setCustomSource] = useState('');
  const [editingSource, setEditingSource] = useState(false);
  const [savingSource, setSavingSource] = useState(false);
  const [sourceType, setSourceType] = useState('url'); // 'url' or 'book'
  const [bookSource, setBookSource] = useState({ title: '', author: '', page: '' });
  
  // Inline title editing state
  const [editingTitle, setEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  
  // Labels state
  const [userLabels, setUserLabels] = useState([]);
  const [recipeLabels, setRecipeLabels] = useState([]);
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [nutritionPending, setNutritionPending] = useState(false);
  
  // Check if we came from the chat flow, buzz, browse, feed, or hive
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isFromChat = searchParams.get('fromChat') === 'true';
    const isFromBuzz = searchParams.get('fromBuzz') === 'true';
    const isFromBrowse = searchParams.get('fromBrowse') === 'true';
    const isFromFeed = searchParams.get('fromFeed') === 'true';
    const isFromHive = searchParams.get('fromHive');
    const hiveFilter = searchParams.get('hiveFilter');
    
    // Also check location.state for hive info (passed when navigating from HiveDetailPage)
    const stateFromHive = location.state?.fromHive;
    const stateHiveName = location.state?.hiveName;
    
    // Check if we should open the Buzz modal (returning from hive discovery)
    const shouldOpenBuzz = location.state?.openBuzzModal;
    
    if (isFromChat) {
      setCameFromChat(true);
      // Show the cook-along tip after a short delay (only the chat tip, not the cook button tip)
      setTimeout(() => setShowCookAlongTip(true), 500);
      // Clean up URL without reloading
      window.history.replaceState({}, '', `/recipe/${recipeId}`);
    }
    if (isFromBuzz) {
      setCameFromBuzz(true);
      // Clean up URL without reloading
      window.history.replaceState({}, '', `/recipe/${recipeId}`);
    }
    if (isFromBrowse) {
      setCameFromBrowse(true);
      // Clean up URL without reloading
      window.history.replaceState({}, '', `/recipe/${recipeId}`);
    }
    if (isFromFeed) {
      setCameFromFeed(true);
      if (hiveFilter) {
        setFeedHiveFilter(hiveFilter);
      }
      // Clean up URL without reloading
      window.history.replaceState({}, '', `/recipe/${recipeId}`);
    }
    if (isFromHive || stateFromHive) {
      setCameFromHive(true);
      setFromHiveId(isFromHive || stateFromHive);
      if (stateHiveName) {
        setFromHiveName(stateHiveName);
      }
      // Clean up URL without reloading
      window.history.replaceState({}, '', `/recipe/${recipeId}`);
    }
    
    // Open Buzz modal if returning from hive discovery
    if (shouldOpenBuzz) {
      // Clear the state first to prevent re-opening on refresh
      window.history.replaceState({}, '', `/recipe/${recipeId}`);
      
      // Restore saved buzz state if available
      const savedState = location.state?.buzzState;
      if (savedState) {
        if (savedState.caption) setBuzzCaption(savedState.caption);
        if (savedState.photo) setBuzzPhoto(savedState.photo);
        if (savedState.rating) setBuzzRating(savedState.rating);
        if (savedState.selectedHives) setSelectedHives(savedState.selectedHives);
        if (savedState.showInMyBuzz !== undefined) setShowInMyBuzz(savedState.showInMyBuzz);
        if (savedState.sourceType) setBuzzSourceType(savedState.sourceType);
        if (savedState.sourceUrl) setBuzzSourceUrl(savedState.sourceUrl);
        if (savedState.bookSource) setBuzzBookSource(savedState.bookSource);
      }
      
      // Open the buzz modal after a short delay to allow page to load
      setTimeout(() => {
        openBuzzModal();
      }, 500);
    }
    
    // Show Cook button tip when:
    // 1. First time viewing ANY recipe (v2 tip not seen) - EXCEPT when coming from Browse/Feed/Buzz/Hive
    // 2. User just created their FIRST recipe (isFirstRecipe flag)
    // The modal explains "Up Next" and "Currently Cooking" features
    const hasSeenTipV2 = localStorage.getItem('hasSeenCookButtonTipV2');
    const isFirstRecipeVisit = location.state?.isFirstRecipe;
    
    // Show from Chat if it's their first recipe ever
    // Don't show from Browse/Feed/Buzz/Hive (these are discovery flows)
    const shouldShowTip = !hasSeenTipV2 || isFirstRecipeVisit;
    const isFromDiscovery = isFromBrowse || isFromFeed || isFromBuzz || isFromHive || stateFromHive;
    
    if (shouldShowTip && !isFromDiscovery && !shouldOpenBuzz) {
      const timer = setTimeout(() => {
        setShowCurrentlyMealTip(true);
        localStorage.setItem('hasSeenCookButtonTipV2', 'true');
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [recipeId, location.state]);

  // Show welcome message if user just added a shared recipe (new user flow)
  useEffect(() => {
    const welcomeMessage = location.state?.welcomeMessage;
    if (welcomeMessage) {
      toast.success(welcomeMessage, {
        icon: '🍯',
        duration: 4000,
      });
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.welcomeMessage]);

  // Listen for event to re-show the tip modal when How Honey Works is opened
  useEffect(() => {
    const handleShowHelpTiles = () => {
      // Clear the "has seen" flag so the tip can show again
      localStorage.removeItem('hasSeenCookButtonTipV2');
      // Show the modal
      setShowCurrentlyMealTip(true);
    };
    
    window.addEventListener(SHOW_HELP_TILES_EVENT, handleShowHelpTiles);
    return () => window.removeEventListener(SHOW_HELP_TILES_EVENT, handleShowHelpTiles);
  }, []);

  // Helper function to scale a quantity string
  const scaleQuantity = (quantity, scaleFactor) => {
    if (!quantity) return quantity;
    
    // Handle "to taste", "pinch", etc.
    const nonScalable = ['to taste', 'pinch', 'dash', 'some', 'as needed'];
    if (nonScalable.some(term => quantity.toLowerCase().includes(term))) {
      return quantity;
    }
    
    // Unicode fraction to decimal mapping
    const unicodeFractions = {
      '½': 0.5, '⅓': 0.333, '⅔': 0.667, '¼': 0.25, '¾': 0.75,
      '⅕': 0.2, '⅖': 0.4, '⅗': 0.6, '⅘': 0.8,
      '⅙': 0.167, '⅚': 0.833, '⅐': 0.143, '⅛': 0.125,
      '⅜': 0.375, '⅝': 0.625, '⅞': 0.875, '⅑': 0.111, '⅒': 0.1
    };
    
    // Format function to convert decimals to fractions
    const formatScaled = (num) => {
      // Check for common fractions - find the CLOSEST match
      const fractions = [
        [0.125, '⅛'], [0.167, '⅙'], [0.2, '⅕'], [0.25, '¼'], [0.333, '⅓'], [0.375, '⅜'],
        [0.4, '⅖'], [0.5, '½'], [0.6, '⅗'], [0.625, '⅝'], [0.667, '⅔'], [0.75, '¾'], 
        [0.8, '⅘'], [0.833, '⅚'], [0.875, '⅞']
      ];
      
      const whole = Math.floor(num);
      const decimal = num - whole;
      
      // Find the closest fraction within tolerance
      let closestFrac = null;
      let closestDiff = 0.05; // tolerance threshold
      
      for (const [dec, frac] of fractions) {
        const diff = Math.abs(decimal - dec);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestFrac = frac;
        }
      }
      
      if (closestFrac) {
        return whole > 0 ? `${whole} ${closestFrac}` : closestFrac;
      }
      
      // If whole number, return as is
      if (Math.abs(decimal) < 0.05) {
        return String(whole);
      }
      
      // Otherwise, round to reasonable precision
      if (num < 1) {
        return num.toFixed(2).replace(/\.?0+$/, '');
      }
      return num.toFixed(1).replace(/\.?0+$/, '');
    };
    
    // Try to parse the quantity
    let value = 0;
    const trimmed = String(quantity).trim();
    
    // First, convert any Unicode fractions to decimals for parsing
    let normalized = trimmed;
    for (const [unicode, decimal] of Object.entries(unicodeFractions)) {
      if (normalized.includes(unicode)) {
        // Handle mixed numbers like "1 ½" or "1½"
        // Replace "1 ½" with "1+0.5" style for later addition
        normalized = normalized.replace(new RegExp(`(\\d+)\\s*${unicode}`, 'g'), (match, whole) => {
          return String(parseFloat(whole) + decimal);
        });
        // Replace standalone unicode fractions
        normalized = normalized.replace(unicode, String(decimal));
      }
    }
    
    // Now handle slash fractions like "1/2" or "1 1/2"
    if (normalized.includes('/')) {
      // If no scaling needed and original had nice fractions, return as-is
      if (scaleFactor === 1 && !trimmed.match(/\d+\.\d+/)) return quantity;
      
      const parts = normalized.split(' ');
      let total = 0;
      
      for (const part of parts) {
        if (part.includes('/')) {
          const [num, denom] = part.split('/').map(Number);
          if (!isNaN(num) && !isNaN(denom) && denom !== 0) {
            total += num / denom;
          }
        } else {
          const parsed = parseFloat(part);
          if (!isNaN(parsed)) {
            total += parsed;
          }
        }
      }
      value = total;
    } else {
      // Try parsing as a number (may have been converted from unicode)
      value = parseFloat(normalized);
    }
    
    if (isNaN(value) || value === 0) return quantity;
    
    // Format the base value (even with scaleFactor === 1) to convert decimals to fractions
    const scaled = value * scaleFactor;
    
    return formatScaled(scaled);
  };

  // Standalone fetch function for refreshing recipe data
  const fetchRecipe = async () => {
    try {
      const response = await recipeApi.getById(recipeId);
      if (isMountedRef.current) {
        setRecipe(response.data);
        setFullDataLoaded(true);
      }
    } catch (error) {
      if (isMountedRef.current && (error.response?.status === 404 || error.response?.status === 401)) {
        alert(`fetchRecipe failed (${error.response?.status}). Returning to Recipe Box.`);
        navigate('/recipe-box');
      }
    }
  };

  useEffect(() => {
    // Reset scroll position on mount to ensure consistent starting position
    window.scrollTo(0, 0);
    
    // One-time cleanup of old scroll position localStorage entries
    if (!localStorage.getItem('scrollCacheCleared')) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('recipeScroll_')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.setItem('scrollCacheCleared', 'true');
    }
    
    // Always fetch full recipe data (passedRecipe might be lightweight cache without instructions)
    const loadRecipe = async () => {
      // Prevent duplicate fetches (can happen with React StrictMode)
      if (fetchInProgressRef.current) return;
      fetchInProgressRef.current = true;
      
      try {
        const response = await recipeApi.getById(recipeId);
        if (isMountedRef.current) {
          setRecipe(response.data);
          setFullDataLoaded(true);
        }
        recipeApi.markViewed(recipeId).catch(() => {});
      } catch (error) {
        if (isMountedRef.current && (error.response?.status === 404 || error.response?.status === 401)) {
          alert(`Recipe load failed (${error.response?.status}). Returning to Recipe Box.`);
          navigate('/recipe-box');
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
        fetchInProgressRef.current = false;
      }
    };
    
    const loadLabels = async () => {
      try {
        const response = await labelsApi.getAll();
        if (isMountedRef.current) {
          setUserLabels(response.data.labels || []);
        }
      } catch (error) {
        // Silent fail for labels
      }
    };
    
    loadRecipe();
    loadLabels();
  }, [recipeId, navigate]);

  useEffect(() => {
    if (recipe) {
      setNotes(recipe.notes || '');
      setRating(recipe.average_rating || 0);  // Now using average from buzzes
      setRatingCount(recipe.rating_count || 0);
      setCooked(recipe.cooked || false);
      setCurrentMeal(recipe.current_meal || false);
      setUpNext(recipe.up_next || false);
      setRecipeLabels(recipe.labels || []);
      setCustomSource(recipe.custom_source || '');
      
      // Clear stale checkmarks if recipe is no longer in "Currently Cooking"
      if (!recipe.current_meal) {
        const savedChecks = localStorage.getItem(`cookingChecks_${recipeId}`);
        if (savedChecks) {
          localStorage.removeItem(`cookingChecks_${recipeId}`);
          setCheckedIngredients(new Set());
        }
      }
      
      // Helper to detect if a string looks like a URL
      const looksLikeUrl = (str) => {
        if (!str) return false;
        return /^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.(com|org|net|io|co|edu|gov|me|app|dev|cooking|recipes))/.test(str);
      };
      
      // Parse source type - priority: book > url detection > personal
      if (recipe.book_source) {
        setSourceType('book');
        setBookSource(recipe.book_source);
      } else if (recipe.custom_source) {
        // Check if custom_source looks like a URL
        if (looksLikeUrl(recipe.custom_source)) {
          setSourceType('url');
        } else {
          setSourceType('personal');
        }
      }
      
      // Fetch computed tweaks if recipe has a source
      // Backend will return empty tweaks if user is the source owner
      if (recipe.source_recipe_id || recipe.source_url) {
        recipeApi.getTweaks(recipeId).then(response => {
          if (isMountedRef.current) {
            setComputedTweaks(response.data.tweaks || []);
          }
        }).catch(err => {
          console.error('Failed to fetch tweaks:', err);
        });
        
        // Fetch cook count for recipes with a source
        if (recipe.source_recipe_id) {
          shared.getBuzzHistory(recipe.source_recipe_id).then(response => {
            if (isMountedRef.current) {
              setCookCount(response.data.cook_count || 0);
            }
          }).catch(err => {
            console.error('Failed to fetch cook count:', err);
          });
        }
      }
      
      // Always fetch buzz count for this recipe
      buzzApi.getRecipeCount(recipeId).then(response => {
        if (isMountedRef.current) {
          setBuzzCount(response.data.count || 0);
        }
      }).catch(err => {
        console.error('Failed to fetch buzz count:', err);
      });
    }
  }, [recipe, recipeId]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleIngredient = (index) => {
    const newChecked = new Set(checkedIngredients);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedIngredients(newChecked);
  };

  const handleRatingChange = async (newRating) => {
    setRating(newRating);
    try {
      await recipeApi.update(recipeId, { rating: newRating });
    } catch (error) {
      console.error('Failed to update rating:', error);
    }
  };

  // Flower/bee confetti celebration for marking recipe as cooked
  const triggerCookedCelebration = () => {
    // Alternating celebration messages
    const messages = [
      '🐝 Sweet!',
      '🐝 Bee-utifully done!',
    ];
    const message = messages[Math.floor(Math.random() * messages.length)];
    
    // Create confetti burst
    const duration = 2000;
    const end = Date.now() + duration;
    
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#fcd34d', '#fef3c7'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#fcd34d', '#fef3c7'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
    
    // Show sweet toast with bee emoji
    toast.success(message, {
      description: 'Another recipe in the honey pot!',
      duration: 3000,
    });
  };

  const handleCookedChange = async (newCooked) => {
    setCooked(newCooked);
    try {
      await recipeApi.update(recipeId, { cooked: newCooked });
      
      // Trigger celebration when marking as cooked (not when unmarking)
      if (newCooked) {
        triggerCookedCelebration();
      }
    } catch (error) {
      console.error('Failed to update cooked status:', error);
    }
  };

  const handleToggleCurrentMeal = async () => {
    // Optimistic update - change UI immediately
    const newValue = !currentMeal;
    setCurrentMeal(newValue);
    
    // Mutual exclusivity: if adding to Currently Cooking, remove from Up Next
    if (newValue && upNext) {
      setUpNext(false);
    }
    
    // Clear checked ingredients when removing from Currently Cooking
    if (!newValue) {
      setCheckedIngredients(new Set());
      localStorage.removeItem(`cookingChecks_${recipeId}`);
    }
    
    // Update localStorage cache so RecipeBox picks up the change
    try {
      const cached = localStorage.getItem('recipesCache');
      if (cached) {
        const recipes = JSON.parse(cached);
        const updated = recipes.map(r => 
          r.recipe_id === recipeId 
            ? { ...r, current_meal: newValue, up_next: newValue ? false : r.up_next } 
            : r
        );
        localStorage.setItem('recipesCache', JSON.stringify(updated));
        localStorage.setItem('recipesCacheTime', String(Date.now()));
      }
    } catch (e) {
      // Silent fail for cache update
    }
    
    try {
      // Pass explicit value to be idempotent - backend handles mutual exclusivity
      await recipeApi.toggleCurrentMeal(recipeId, newValue);
      // Track analytics
      if (newValue) {
        trackCurrentlyCookingAdded(recipeId);
      } else {
        trackCurrentlyCookingRemoved(recipeId);
      }
    } catch (error) {
      console.error('Failed to toggle current meal:', error);
      // Revert on error
      setCurrentMeal(!newValue);
      if (newValue) {
        setUpNext(upNext); // Restore up_next state
      }
    }
  };
  
  const handleToggleUpNext = async () => {
    // Optimistic update - change UI immediately
    const newValue = !upNext;
    setUpNext(newValue);
    
    // Mutual exclusivity: if adding to Up Next, remove from Currently Cooking
    if (newValue && currentMeal) {
      setCurrentMeal(false);
    }
    
    // Update localStorage cache so RecipeBox picks up the change
    try {
      const cached = localStorage.getItem('recipesCache');
      if (cached) {
        const recipes = JSON.parse(cached);
        const updated = recipes.map(r => 
          r.recipe_id === recipeId 
            ? { ...r, up_next: newValue, current_meal: newValue ? false : r.current_meal } 
            : r
        );
        localStorage.setItem('recipesCache', JSON.stringify(updated));
        localStorage.setItem('recipesCacheTime', String(Date.now()));
      }
    } catch (e) {
      // Silent fail for cache update
    }
    
    try {
      // Pass explicit value to be idempotent - backend handles mutual exclusivity
      await recipeApi.toggleUpNext(recipeId, newValue);
      
      // If added to Up Next, navigate back to Recipe Box and scroll to Up Next section
      if (newValue) {
        navigate('/recipe-box', { state: { scrollToUpNext: true } });
      }
    } catch (error) {
      console.error('Failed to toggle up next:', error);
      // Revert on error
      setUpNext(!newValue);
      if (newValue) {
        setCurrentMeal(currentMeal); // Restore current_meal state
      }
    }
  };

  const handleNotesBlur = async () => {
    if (notes === recipe.notes) return;
    try {
      await recipeApi.update(recipeId, { notes });
    } catch (error) {
      console.error('Failed to update notes:', error);
    }
  };

  // Save custom source handler
  const handleSaveCustomSource = async () => {
    setSavingSource(true);
    try {
      let updateData = {};
      
      if (sourceType === 'url') {
        if (!customSource.trim()) {
          setSavingSource(false);
          return;
        }
        updateData = { 
          custom_source: customSource.trim(),
          book_source: null // Clear book source if switching to URL
        };
        setRecipe(prev => ({ ...prev, custom_source: customSource.trim(), book_source: null }));
      } else if (sourceType === 'book') {
        // Book source
        if (!bookSource.title.trim()) {
          toast.error('Please enter a book title');
          setSavingSource(false);
          return;
        }
        updateData = { 
          book_source: {
            title: bookSource.title.trim(),
            author: bookSource.author.trim(),
            page: bookSource.page.trim()
          },
          custom_source: null // Clear URL source if switching to book
        };
        setRecipe(prev => ({ ...prev, book_source: updateData.book_source, custom_source: null }));
      } else {
        // Personal source
        if (!customSource.trim()) {
          setSavingSource(false);
          return;
        }
        updateData = { 
          custom_source: customSource.trim(),
          book_source: null // Clear book source if switching to personal
        };
        setRecipe(prev => ({ ...prev, custom_source: customSource.trim(), book_source: null }));
      }
      
      await recipeApi.update(recipeId, updateData);
      setEditingSource(false);
      toast.success('Source saved!');
    } catch (error) {
      console.error('Failed to save source:', error);
      toast.error('Failed to save source');
    } finally {
      setSavingSource(false);
    }
  };

  // Save title handler
  const handleSaveTitle = async () => {
    if (!editedTitle.trim() || editedTitle.trim() === recipe.title) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      await recipeApi.update(recipeId, { title: editedTitle.trim() });
      setRecipe(prev => ({ ...prev, title: editedTitle.trim() }));
      setEditingTitle(false);
      toast.success('Title updated!');
    } catch (error) {
      console.error('Failed to save title:', error);
      toast.error('Failed to save title');
    } finally {
      setSavingTitle(false);
    }
  };

  // Share recipe handler
  const handleShareRecipe = async () => {
    setSharing(true);
    try {
      const response = await recipeApi.share(recipeId);
      // Use OG endpoint for sharing (better link previews), which redirects to actual page
      const ogShareUrl = `${window.location.origin}/api/og${response.data.share_url}`;
      const directUrl = `${window.location.origin}${response.data.share_url}`;
      
      // Try Web Share API (works on mobile)
      if (navigator.share) {
        try {
          await navigator.share({
            title: recipe.title,
            text: `Check out ${recipe.title} on Honey!`,
            url: ogShareUrl,
          });
        } catch (shareError) {
          // If share was cancelled or failed, fall back to clipboard
          if (shareError.name !== 'AbortError') {
            try {
              await navigator.clipboard.writeText(ogShareUrl);
              toast.success('Share link copied!');
            } catch (clipErr) {
              // Clipboard might fail on some mobile browsers
              toast.info(`Share this link: ${directUrl}`);
            }
          }
        }
      } else {
        // Desktop: Just copy the OG URL to clipboard
        try {
          await navigator.clipboard.writeText(ogShareUrl);
          toast.success('Share link copied!');
        } catch (clipErr) {
          toast.info(`Share this link: ${directUrl}`);
        }
      }
    } catch (error) {
      console.error('Failed to share recipe:', error);
      // More specific error message
      if (error.response?.status === 401) {
        alert('Please log in again to share recipes.');
      } else if (error.response?.status === 404) {
        alert('Recipe not found. Please refresh and try again.');
      } else {
        alert('Failed to share recipe. Please check your connection and try again.');
      }
    } finally {
      setSharing(false);
    }
  };
  
  // Label management functions
  const handleAddLabel = async (labelName) => {
    if (recipeLabels.includes(labelName)) return;
    const newLabels = [...recipeLabels, labelName];
    setRecipeLabels(newLabels);
    setShowLabelDropdown(false);
    try {
      await recipeApi.updateLabels(recipeId, newLabels);
    } catch (error) {
      console.error('Failed to add label:', error);
      setRecipeLabels(recipeLabels); // Revert on error
    }
  };
  
  const handleRemoveLabel = async (labelName) => {
    const newLabels = recipeLabels.filter(l => l !== labelName);
    setRecipeLabels(newLabels);
    try {
      await recipeApi.updateLabels(recipeId, newLabels);
    } catch (error) {
      console.error('Failed to remove label:', error);
      setRecipeLabels(recipeLabels); // Revert on error
    }
  };
  
  const handleCreateAndAddLabel = async () => {
    if (!newLabelName.trim()) return;
    setCreatingLabel(true);
    try {
      const res = await labelsApi.create(newLabelName.trim());
      const newLabel = res.data.label;
      setUserLabels([...userLabels, newLabel]);
      await handleAddLabel(newLabel.name);
      setNewLabelName('');
    } catch (error) {
      console.error('Failed to create label:', error);
    } finally {
      setCreatingLabel(false);
    }
  };
  
  const getLabelInfo = (labelName) => {
    const label = userLabels.find(l => l.name === labelName);
    return label || { name: labelName, color: '#6b7280', icon: null };
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Please upload a JPEG, PNG, or WEBP image');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64 = event.target.result.split(',')[1];
        await recipeApi.addImage(recipeId, base64);
        fetchRecipe();
        
        // Offer to share to Buzz
        setBuzzPhoto(base64);
        openBuzzModal();
      } catch (error) {
        console.error('Failed to upload image:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Open buzz modal and load user's hives
  const openBuzzModal = async () => {
    setShowBuzzModal(true);
    setLoadingHives(true);
    try {
      const res = await hivesApi.getMyHives();
      // Handle new response format with active/my_created/joined/dormant
      // Include both active AND dormant hives for buzzing (buzzing to dormant wakes them up)
      if (res.data?.all_hives !== undefined) {
        const activeHives = res.data.all_hives || [];
        const dormantHives = (res.data.dormant || []).map(h => ({ ...h, is_dormant: true }));
        setMyHives([...activeHives, ...dormantHives]);
      } else if (res.data?.active !== undefined) {
        setMyHives(res.data.active || []);
      } else {
        setMyHives(res.data || []);
      }
    } catch (error) {
      console.error('Failed to load hives:', error);
    } finally {
      setLoadingHives(false);
    }
  };
  
  // Toggle hive selection
  const toggleHiveSelection = (hiveId) => {
    setSelectedHives(prev => 
      prev.includes(hiveId) 
        ? prev.filter(id => id !== hiveId)
        : [...prev, hiveId]
    );
  };
  
  const handleShareToBuzz = async () => {
    setSharingToBuzz(true);
    try {
      // If there's a new photo that's not already on the recipe, add it to the recipe too
      const isNewPhoto = buzzPhoto && (!recipe.images || !recipe.images.includes(buzzPhoto));
      
      if (isNewPhoto) {
        // Add photo to recipe card first
        await recipeApi.addImage(recipeId, buzzPhoto);
      }
      
      const buzzData = {
        recipe_id: recipeId,
        caption: buzzCaption,
        media: buzzPhoto ? [{ type: 'image', data: buzzPhoto }] : [],
        hive_ids: selectedHives,
        show_in_my_buzz: showInMyBuzz,
        rating: buzzRating > 0 ? buzzRating : null  // Only include if rated
      };
      
      await buzzApi.create(buzzData);
      
      // Refresh recipe to show new photo and updated rating
      fetchRecipe();
      
      // Update buzz count immediately
      setBuzzCount(prev => prev + 1);
      
      // Update average rating if this buzz had a rating
      if (buzzRating > 0) {
        setAverageRating(prev => {
          const newCount = ratingCount + 1;
          const newAvg = ((prev * ratingCount) + buzzRating) / newCount;
          setRatingCount(newCount);
          return newAvg;
        });
      }
      
      setShowBuzzModal(false);
      setBuzzCaption('');
      setBuzzPhoto(null);
      setBuzzRating(0);
      setSelectedHives([]);
      setShowInMyBuzz(true);
      
      // Show success feedback - build message based on what was selected
      let toastMessage = '🐝 Saved to ';
      if (showInMyBuzz && selectedHives.length > 0) {
        // Both My Buzz and hives
        toastMessage += `your cooking journal and ${selectedHives.length} hive${selectedHives.length > 1 ? 's' : ''}!`;
      } else if (showInMyBuzz) {
        // Only My Buzz
        toastMessage += 'your cooking journal!';
      } else if (selectedHives.length > 0) {
        // Only hives (no My Buzz)
        toastMessage += `${selectedHives.length} hive${selectedHives.length > 1 ? 's' : ''}!`;
      }
      toast.success(toastMessage);
    } catch (error) {
      console.error('Failed to save to Buzz:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSharingToBuzz(false);
    }
  };
  
  const handleDeletePhoto = async (photoIndex) => {
    try {
      await recipeApi.deleteImage(recipeId, photoIndex);
      fetchRecipe();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const handleRecipeUpdate = async (updatedData) => {
    try {
      await recipeApi.update(recipeId, updatedData);
      fetchRecipe();
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update recipe:', error);
      alert('Failed to update recipe. Please try again.');
    }
  };

  const handleDeleteRecipe = async () => {
    try {
      await recipeApi.delete(recipeId);
      navigate('/recipe-box');
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      alert('Failed to delete recipe. Please try again.');
    }
  };

  const handleRestoreRecipe = async () => {
    try {
      const response = await recipeApi.restore(recipeId);
      await fetchRecipe(); // Refresh to update deleted status
    } catch (error) {
      console.error('Failed to restore recipe:', error);
      alert(`Failed to restore recipe: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Handle showing the update preview from the cook-along chat
  const handleShowUpdatePreview = async (updatedRecipe, originalTitle, modificationDescription = null) => {
    // Skip preview - directly save the updated recipe
    try {
      const updateData = {
        title: updatedRecipe.title,
        description: updatedRecipe.description,
        ingredients: updatedRecipe.ingredients,
        instructions: updatedRecipe.instructions,
        prep_time: updatedRecipe.prep_time,
        cook_time: updatedRecipe.cook_time,
        servings: updatedRecipe.servings,
        category: updatedRecipe.category,
        recipe_tips: updatedRecipe.recipe_tips,
      };
      
      // Add the modification to the list if provided, using AI to consolidate
      // Track modifications if recipe has a source (URL, Honey user, or other source)
      if (modificationDescription && (recipe.source_url || recipe.source_owner_name || recipe.source_recipe_id)) {
        const currentMods = recipe.modifications || [];
        
        if (currentMods.length > 0) {
          // Use AI to consolidate modifications
          try {
            const consolidateResponse = await ai.summarizeText(
              `Current modifications:\n${currentMods.map(m => `- ${m}`).join('\n')}\n\nNew modification:\n- ${modificationDescription}`,
              `Consolidate these recipe modifications into a clean, non-redundant list. Remove contradictions (e.g., "add X" then "remove X" = remove both). Combine related changes. Return ONLY a JSON array of strings, no explanation. Return consolidated list as JSON array like: ["modification 1", "modification 2"]`
            );
            
            // Try to parse the AI response as JSON
            const responseText = consolidateResponse.data.summary || '';
            const jsonMatch = responseText.match(/\[[\s\S]*\]/);
            if (jsonMatch) {
              const consolidated = JSON.parse(jsonMatch[0]);
              updateData.modifications = consolidated;
            } else {
              // Fallback: just append
              updateData.modifications = [...currentMods, modificationDescription];
            }
          } catch (consolidateError) {
            console.error('Failed to consolidate modifications:', consolidateError);
            // Fallback: just append
            updateData.modifications = [...currentMods, modificationDescription];
          }
        } else {
          // First modification, just add it
          updateData.modifications = [modificationDescription];
        }
      }
      
      await recipeApi.update(recipeId, updateData);
      // Refresh the recipe to show updated content
      await fetchRecipe();
      // Flag that nutrition is being recalculated in the background
      if (updateData.ingredients) {
        setNutritionPending(true);
      }
      // Return success with info about what changed
      const titleChanged = updatedRecipe.title !== originalTitle;
      return { 
        success: true, 
        titleChanged,
        newTitle: titleChanged ? updatedRecipe.title : null
      };
    } catch (error) {
      console.error('Failed to save updated recipe:', error);
      return { success: false, error: 'Failed to save recipe. Please try again.' };
    }
  };

  // Handle saving the updated recipe from the preview (keeping for backward compatibility)
  const handleSaveUpdatedRecipe = async (updatedRecipeData) => {
    try {
      await recipeApi.update(recipeId, {
        title: updatedRecipeData.title,
        description: updatedRecipeData.description,
        ingredients: updatedRecipeData.ingredients,
        instructions: updatedRecipeData.instructions,
        prep_time: updatedRecipeData.prep_time,
        cook_time: updatedRecipeData.cook_time,
        servings: updatedRecipeData.servings,
        category: updatedRecipeData.category,
        recipe_tips: updatedRecipeData.recipe_tips,
      });
      // Refresh and go back to detail view
      await fetchRecipe();
      setShowUpdatePreview(false);
      setUpdatePreviewRecipe(null);
    } catch (error) {
      console.error('Failed to save updated recipe:', error);
      alert('Failed to save recipe. Please try again.');
      throw error;
    }
  };

  // Handle going back from the preview to the detail view
  const handleBackFromPreview = () => {
    setShowUpdatePreview(false);
    setUpdatePreviewRecipe(null);
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading recipe..." fullScreen />
    );
  }

  if (!recipe) {
    return null;
  }

  // Show the update preview when the user clicks "Update Recipe" in the chat
  if (showUpdatePreview && updatePreviewRecipe) {
    return (
      <RecipePreview
        recipe={updatePreviewRecipe}
        onSave={handleSaveUpdatedRecipe}
        onBack={handleBackFromPreview}
        isUpdate={true}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-20">
      {/* Header */}
      <header ref={headerRef} className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 md:py-4">
          {/* Mobile Layout */}
          <div className="sm:hidden">
            {/* Row 1: Navigation + Action icons */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                {/* When coming from Chat: show Recipe Box as primary back target */}
                {cameFromChat ? (
                  <button
                    onClick={() => navigate('/recipe-box')}
                    className="flex items-center gap-1 text-gray-500 hover:text-amber-600 transition-colors"
                    data-testid="back-btn-mobile"
                  >
                    <ChevronDown size={20} className="rotate-90" />
                    <span className="text-xs">Recipes</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (cameFromHive && fromHiveId) navigate(`/hive/${fromHiveId}`);
                      else if (cameFromFeed) navigate(feedHiveFilter ? `/feed?hive=${feedHiveFilter}` : '/feed');
                      else if (cameFromBrowse) navigate('/browse');
                      else if (cameFromBuzz) navigate('/buzz');
                      else navigate('/recipe-box');
                    }}
                    className="flex items-center gap-1 text-gray-500 hover:text-amber-600 transition-colors"
                    data-testid="back-btn-mobile"
                  >
                    <ChevronDown size={20} className="rotate-90" />
                    <span className="text-xs">
                      {cameFromHive ? (fromHiveName || 'Hive') : cameFromFeed ? 'Feed' : cameFromBrowse ? 'Browse' : cameFromBuzz ? 'Buzz' : 'Recipes'}
                    </span>
                  </button>
                )}
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={handleShareRecipe}
                  disabled={sharing}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  title="Share recipe"
                  data-testid="share-recipe-btn-mobile"
                >
                  {sharing ? <Loader size={18} className="animate-spin" /> : <ExternalLink size={18} />}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrintModal(true)}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  title="Print recipe"
                >
                  <Printer size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(true)}
                  data-testid="edit-recipe-button-mobile"
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Edit size={18} />
                </button>
                <button 
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Camera size={18} />
                </button>
                {recipe.deleted ? (
                  <button
                    type="button"
                    onClick={handleRestoreRecipe}
                    className="p-1.5 hover:bg-emerald-50 text-gray-500 hover:text-emerald-600 rounded-full transition-colors"
                    title="Restore recipe"
                  >
                    <RotateCcw size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleDeleteRecipe}
                    className="p-1.5 hover:bg-gray-100 text-gray-500 hover:text-gray-700 rounded-full transition-colors"
                    title="Delete recipe"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
            {/* Row 2: Full Title - clickable to edit */}
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="flex-1 font-bold text-gray-900 text-lg leading-tight px-2 py-1 border border-amber-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') setEditingTitle(false);
                  }}
                  data-testid="edit-title-input"
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={savingTitle}
                  className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  {savingTitle ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingTitle(false)}
                  className="px-2 py-1 text-gray-500 text-sm hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <h1 
                className="font-bold text-gray-900 text-lg leading-tight cursor-pointer hover:text-amber-600 transition-colors group"
                onClick={() => {
                  setEditedTitle(recipe.title);
                  setEditingTitle(true);
                }}
                title="Click to edit title"
              >
                {recipe.title}
                <Edit size={14} className="inline-block ml-2 opacity-0 group-hover:opacity-50 transition-opacity" />
              </h1>
            )}
          </div>
          
          {/* Desktop: Title row with action icons inline */}
          <div className="hidden sm:flex items-center justify-between gap-2">
            {editingTitle ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="flex-1 font-bold text-gray-900 text-lg sm:text-2xl px-2 py-1 border border-amber-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') setEditingTitle(false);
                  }}
                  data-testid="edit-title-input-desktop"
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={savingTitle}
                  className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 font-medium"
                >
                  {savingTitle ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingTitle(false)}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <h1 
                className="font-bold text-gray-900 line-clamp-1 flex-1 text-lg sm:text-2xl cursor-pointer hover:text-amber-600 transition-colors group"
                onClick={() => {
                  setEditedTitle(recipe.title);
                  setEditingTitle(true);
                }}
                title="Click to edit title"
              >
                {recipe.title}
                <Edit size={16} className="inline-block ml-2 opacity-0 group-hover:opacity-50 transition-opacity" />
              </h1>
            )}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={handleShareRecipe}
                disabled={sharing}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Share recipe"
                data-testid="share-recipe-btn"
              >
                {sharing ? <Loader size={20} className="animate-spin" /> : <ExternalLink size={20} />}
              </button>
              <button
                type="button"
                onClick={() => setShowPrintModal(true)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Print recipe"
              >
                <Printer size={20} />
              </button>
              <button
                type="button"
                onClick={() => setShowEditModal(true)}
                data-testid="edit-recipe-button"
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Edit size={20} />
              </button>
              {/* Photo Upload */}
              <div className="relative">
                <button 
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer inline-flex"
                  onClick={() => {
                    if (!localStorage.getItem('seenPhotoTip')) {
                      setShowPhotoTip(true);
                      localStorage.setItem('seenPhotoTip', 'true');
                    }
                    fileInputRef.current?.click();
                  }}
                  disabled={uploading}
                >
                  <Camera size={20} />
                </button>
                {showPhotoTip && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-green-200 p-3 z-50 animate-fade-in">
                    <button 
                      onClick={() => setShowPhotoTip(false)}
                      className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-sm"
                    >
                      ✕
                    </button>
                    <div className="flex items-start gap-2">
                      <span className="text-xl">📸</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800 mb-1">Prove you cooked it!</p>
                        <p className="text-xs text-gray-600">Upload a photo of your finished dish. Your cooking memories in one place!</p>
                      </div>
                    </div>
                    <div className="absolute -top-2 right-4 w-3 h-3 bg-white border-l border-t border-green-200 transform rotate-45"></div>
                  </div>
                )}
              </div>
              {recipe.deleted ? (
                <button
                  type="button"
                  onClick={handleRestoreRecipe}
                  data-testid="restore-recipe-button"
                  className="p-2 hover:bg-emerald-50 text-gray-500 hover:text-emerald-600 rounded-full transition-colors"
                  title="Restore recipe"
                >
                  <RotateCcw size={20} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDeleteRecipe}
                  data-testid="delete-recipe-button"
                  className="p-2 hover:bg-gray-100 text-gray-500 hover:text-gray-700 rounded-full transition-colors"
                  title="Delete recipe"
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </div>
          {/* Desktop: Breadcrumb navigation */}
          <div className="hidden sm:flex items-center justify-between mt-2 text-sm">
            {/* Back button - always on LEFT */}
            {/* When coming from Chat: show Recipe Box link first (we want users to discover it) */}
            {cameFromChat ? (
              <>
                <button
                  onClick={() => navigate('/recipe-box')}
                  className="text-gray-500 hover:text-amber-600 transition-colors"
                  data-testid="back-btn-desktop"
                >
                  ← Recipe Box
                </button>
                <button
                  onClick={() => navigate('/create')}
                  className="text-gray-400 hover:text-amber-600 transition-colors"
                >
                  Back to Chat →
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    if (cameFromHive && fromHiveId) navigate(`/hive/${fromHiveId}`);
                    else if (cameFromFeed) navigate(feedHiveFilter ? `/feed?hive=${feedHiveFilter}` : '/feed');
                    else if (cameFromBrowse) navigate('/browse');
                    else if (cameFromBuzz) navigate('/buzz');
                    else navigate('/recipe-box');
                  }}
                  className="text-gray-500 hover:text-amber-600 transition-colors"
                  data-testid="back-btn-desktop"
                >
                  ← {cameFromHive ? `Back to ${fromHiveName || 'Hive'}` : cameFromFeed ? 'Back to Feed' : cameFromBrowse ? 'Back to Browse' : cameFromBuzz ? 'Back to Buzz' : 'Recipe Box'}
                </button>
                <div className="flex items-center gap-4">
                  {/* Show Recipe Box link if we came from somewhere other than Recipe Box */}
                  {(cameFromHive || cameFromFeed || cameFromBrowse || cameFromBuzz) && (
                    <button
                      onClick={() => navigate('/recipe-box')}
                      className="text-gray-400 hover:text-amber-600 transition-colors"
                    >
                      Recipe Box →
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
        {/* Recipe Images or Placeholder */}
        <div className="mb-4 md:mb-6">
          {recipe.images && recipe.images.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {/* Reverse order - newest first (leftmost) */}
              {[...(recipe.images || [])].reverse().map((img, idx) => {
                const originalIdx = recipe.images.length - 1 - idx;
                // Determine image format and build proper src
                let imgSrc = img;
                if (!img.startsWith('data:') && !img.startsWith('http')) {
                  // Raw base64 - detect format from magic bytes
                  if (img.startsWith('/9j/')) {
                    imgSrc = `data:image/jpeg;base64,${img}`;
                  } else if (img.startsWith('iVBOR')) {
                    imgSrc = `data:image/png;base64,${img}`;
                  } else if (img.startsWith('UklGR')) {
                    imgSrc = `data:image/webp;base64,${img}`;
                  } else {
                    // Default to JPEG
                    imgSrc = `data:image/jpeg;base64,${img}`;
                  }
                }
                return (
                  <div key={idx} className="relative flex-shrink-0 group">
                    <img
                      src={imgSrc}
                      alt={`${recipe.title} ${idx + 1}`}
                      className="w-40 h-40 object-cover rounded-lg shadow-md"
                      onError={(e) => {
                        // Hide broken images
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Delete button - hover to show */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeletePhoto(originalIdx);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all z-10 pointer-events-auto cursor-pointer"
                      title="Delete photo"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
              {/* Add more photos button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 w-40 h-40 border-2 border-dashed border-amber-300 rounded-lg flex flex-col items-center justify-center text-amber-600 hover:bg-amber-50 hover:border-amber-400 transition-colors"
              >
                <Camera size={24} className="mb-1" />
                <span className="text-xs">Add Photo</span>
              </button>
            </div>
          ) : (
            <RecipePlaceholder 
              category={recipe.category} 
              size="medium" 
              onAddPhoto={() => fileInputRef.current?.click()}
            />
          )}
          {/* Hidden file input for photo upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageUpload}
            className="hidden"
            disabled={uploading}
          />
        </div>

        {/* Description & Rating */}
        <div className="bg-white rounded-lg shadow-md mb-4 md:mb-6 p-4 md:p-6">
          <div className="space-y-3 mb-3 md:mb-4">
            {/* Row 1: Stars (left) + My Notes (right) */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <StarRating rating={rating} onChange={handleRatingChange} />
                {ratingCount > 0 && (
                  <span className="text-xs text-gray-500 ml-1">({ratingCount} reviews)</span>
                )}
              </div>
              {/* My Notes */}
              <button
                onClick={() => setShowNotes(!showNotes)}
                data-testid="toggle-notes-button"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                📝 My Notes
                {showNotes ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            
            {/* Notes Preview - show when notes exist but section is collapsed */}
            {!showNotes && notes && notes.trim() && (
              <button
                onClick={() => setShowNotes(true)}
                className="w-full text-left bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 hover:bg-amber-100 transition-colors"
              >
                <p className="text-sm text-gray-600 line-clamp-2 italic">
                  "{notes.trim()}"
                </p>
                <span className="text-xs text-amber-600 mt-1 inline-block">Tap to edit</span>
              </button>
            )}
            
            {/* Row 2: Action Buttons - Up Next, Cook, Cooked */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Up Next Toggle */}
              <button
                onClick={handleToggleUpNext}
                data-testid="up-next-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  upNext
                    ? 'bg-blue-500 text-white hover:bg-blue-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                }`}
                title={upNext ? 'Remove from Up Next' : 'Add to Up Next queue'}
              >
                <List size={14} />
                Up Next
              </button>
              
              {/* Currently Cooking Toggle */}
              <button
                onClick={handleToggleCurrentMeal}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  currentMeal 
                    ? 'bg-amber-600 text-white hover:bg-amber-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-600'
                }`}
                title={currentMeal ? 'Remove from Currently Cooking' : 'Add to Currently Cooking for Mise en Place & Shopping List'}
              >
                <ChefHat size={14} />
                {currentMeal ? 'Cooking' : 'Cook'}
              </button>
              
              {/* Cooked Checkbox */}
              <button
                onClick={() => handleCookedChange(!cooked)}
                data-testid="cooked-checkbox"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  cooked 
                    ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' 
                    : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                }`}
              >
                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${cooked ? 'border-emerald-600 bg-emerald-600' : 'border-gray-400'}`}>
                  {cooked && <Check size={12} className="text-white" strokeWidth={3} />}
                </div>
                Cooked
              </button>
            </div>
            
            {/* First Recipe Modal - explains Up Next & Cook buttons */}
            {showCurrentlyMealTip && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-fade-in">
                  <div className="bg-gradient-to-r from-amber-600 to-amber-400 px-6 py-4 text-white">
                    <h2 className="text-xl font-bold">Welcome to your recipe 👋</h2>
                    <p className="text-amber-100 text-sm">Here&apos;s how Honey helps you plan and cook</p>
                  </div>
                  <div className="p-6 space-y-5">
                    {/* Up Next Button Explanation */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-24">
                        {/* Mock Up Next Button */}
                        <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 border border-gray-200 shadow-sm whitespace-nowrap">
                          <List size={14} className="flex-shrink-0" />
                          <span>Up Next</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">
                          Queue recipes you plan to cook next.
                        </p>
                        <p className="text-sm text-gray-600 mb-1">Unlocks:</p>
                        <ul className="text-sm text-gray-600 space-y-0.5">
                          <li>• <strong>Shopping List</strong> – One aisle-sorted list across all queued recipes</li>
                          <li>• <strong>Reorder</strong> – Drag to set the order you&apos;ll cook</li>
                        </ul>
                      </div>
                    </div>
                    
                    {/* Cook Button Explanation */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-24">
                        {/* Mock Cook Button */}
                        <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 border border-gray-200 shadow-sm whitespace-nowrap">
                          <ChefHat size={14} className="flex-shrink-0" />
                          <span>Cook</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-1">
                          Keeps recipes you&apos;re actively cooking pinned at the top.
                        </p>
                        <p className="text-sm text-gray-600 mb-1">Unlocks:</p>
                        <ul className="text-sm text-gray-600 space-y-0.5">
                          <li>• <strong>Concurrent Cooking</strong> – A coordinated timeline across multiple recipes</li>
                          <li>• <strong>Print</strong> – Print multiple recipes at once</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="px-6 pb-6">
                    <button
                      onClick={() => setShowCurrentlyMealTip(false)}
                      className="w-full py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors"
                    >
                      Got it!
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Row 3: Buzz + History */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // Pre-select most recent photo if available
                  if (recipe.images && recipe.images.length > 0) {
                    setBuzzPhoto(recipe.images[recipe.images.length - 1]);
                  }
                  openBuzzModal();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors whitespace-nowrap"
                title="Share to your Buzz"
              >
                <span>🐝</span>
                Buzz
              </button>
              {buzzCount > 0 && (
                <button
                  onClick={() => {
                    if (recipe.source_recipe_id) {
                      // Pass the user's recipe ID so BuzzHistoryPage can navigate back to the correct page
                      navigate(`/shared/${recipe.source_recipe_id}/buzz`, { 
                        state: { fromRecipeId: recipeId } 
                      });
                    } else {
                      navigate(`/buzz?recipe=${recipeId}`);
                    }
                  }}
                  className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
                >
                  {buzzCount} →
                </button>
              )}
              
              {/* Cook count removed - MVP focuses on personal cooking journal */}
            </div>
          </div>
          
          {/* Labels Section */}
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
            <Tag size={16} className="text-gray-400" />
            {recipeLabels
              .filter(labelName => labelName !== 'Up Next') // Hide Up Next - it has its own button
              .map((labelName) => {
              const labelInfo = getLabelInfo(labelName);
              return (
                <span
                  key={labelName}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium text-white group"
                  style={{ backgroundColor: labelInfo.color }}
                >
                  {labelInfo.icon && <span>{labelInfo.icon}</span>}
                  {labelName}
                  <button
                    onClick={() => handleRemoveLabel(labelName)}
                    className="ml-1 opacity-70 hover:opacity-100"
                    title="Remove label"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
            
            {/* Add Label Button/Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowLabelDropdown(!showLabelDropdown)}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-500 border border-dashed border-gray-300 rounded-full hover:border-amber-400 hover:text-amber-600 transition-colors"
              >
                <Plus size={14} />
                Add Label
              </button>
              
              {showLabelDropdown && (
                <div className="absolute z-50 mt-2 w-56 bg-white rounded-lg shadow-lg border p-2 left-0">
                  {/* Available labels */}
                  <div className="max-h-40 overflow-y-auto">
                    {userLabels
                      .filter(label => !recipeLabels.includes(label.name) && label.name !== 'Up Next') // Hide Up Next - it has its own button
                      .map((label) => (
                        <button
                          key={label.name}
                          onClick={() => handleAddLabel(label.name)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
                        >
                          {label.icon ? (
                            <span className="text-sm">{label.icon}</span>
                          ) : (
                            <span 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: label.color }}
                            />
                          )}
                          <span className="text-sm text-gray-700">{label.name}</span>
                        </button>
                      ))}
                    {userLabels.filter(l => !recipeLabels.includes(l.name)).length === 0 && (
                      <p className="text-xs text-gray-500 px-2 py-1">All labels applied</p>
                    )}
                  </div>
                  
                  {/* Create new label */}
                  <div className="border-t mt-2 pt-2">
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={newLabelName}
                        onChange={(e) => setNewLabelName(e.target.value)}
                        placeholder="New label..."
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-amber-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreateAndAddLabel();
                        }}
                      />
                      <button
                        onClick={handleCreateAndAddLabel}
                        disabled={!newLabelName.trim() || creatingLabel}
                        className="px-2 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  
                  {/* Close dropdown when clicking outside */}
                  <button
                    onClick={() => setShowLabelDropdown(false)}
                    className="w-full text-center text-xs text-gray-400 mt-2 hover:text-gray-600"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>

          {recipe.description && (
            <p className="text-gray-600 mb-4">{recipe.description}</p>
          )}

          {showNotes && (
            <div className="mt-4 pt-4 border-t">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleNotesBlur}
                data-testid="recipe-notes-textarea"
                placeholder="Add your cooking notes, modifications, or tips here..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
            {recipe.prep_time > 0 && (
              <div>
                <span className="font-medium">Prep:</span> {recipe.prep_time} min
              </div>
            )}
            {recipe.cook_time > 0 && (
              <div>
                <span className="font-medium">Cook:</span> {recipe.cook_time} min
              </div>
            )}
            {recipe.servings > 0 && (
              <div>
                <span className="font-medium">Servings:</span> {scale === 1 ? recipe.servings : Math.round(recipe.servings * scale)}
              </div>
            )}
            {recipe.difficulty_level && (
              <div>
                <span className="font-medium">Difficulty:</span> {recipe.difficulty_level}
              </div>
            )}
            {/* Recipe Scale Buttons */}
            <div className="flex items-center gap-1 ml-auto">
              <span className="text-xs text-gray-500 mr-1">Scale:</span>
              <button
                onClick={() => setScale(0.5)}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  scale === 0.5
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                ½×
              </button>
              <button
                onClick={() => setScale(1)}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  scale === 1
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                1×
              </button>
              <button
                onClick={() => setScale(2)}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  scale === 2
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                2×
              </button>
            </div>
          </div>
          
          {/* Source URL & Modifications - show lineage info */}
          {(recipe.source_url || recipe.source_owner_name || recipe.custom_source || recipe.book_source || recipe.source_image || computedTweaks.length > 0 || (recipe.modifications && recipe.modifications.length > 0)) && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {/* Show source - priority: source_url > custom_source > source_owner_name */}
              {recipe.source_url ? (
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                  {/* Source Image thumbnail - displayed inline */}
                  {recipe.source_image && (
                    <img 
                      src={recipe.source_image.startsWith('data:') ? recipe.source_image : 
                           recipe.source_image.startsWith('http') ? recipe.source_image :
                           recipe.source_image.startsWith('/9j/') ? `data:image/jpeg;base64,${recipe.source_image}` :
                           recipe.source_image.startsWith('iVBOR') ? `data:image/png;base64,${recipe.source_image}` :
                           `data:image/jpeg;base64,${recipe.source_image}`}
                      alt="Source"
                      className="w-12 h-12 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex items-center gap-2">
                    <span>{computedTweaks.length > 0 ? '✨' : '🔗'}</span>
                    <span>{computedTweaks.length > 0 ? 'Inspired by:' : 'Source:'}</span>
                    <a 
                      href={recipe.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:text-amber-700 hover:underline truncate max-w-xs"
                    >
                      {(() => {
                        try {
                          return new URL(recipe.source_url).hostname;
                        } catch {
                          return recipe.source_url;
                        }
                      })()}
                    </a>
                  </div>
                </div>
              ) : recipe.book_source ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2 flex-wrap">
                  <span>📚</span>
                  <span className="text-gray-700 font-medium">{recipe.book_source.title}</span>
                  {recipe.book_source.author && (
                    <span className="text-gray-500">by {recipe.book_source.author}</span>
                  )}
                  {recipe.book_source.page && (
                    <span className="text-gray-400">• p. {recipe.book_source.page}</span>
                  )}
                  <button
                    onClick={() => {
                      setSourceType('book');
                      setBookSource(recipe.book_source);
                      setEditingSource(true);
                    }}
                    className="ml-1 p-1 text-gray-400 hover:text-amber-600 transition-colors"
                    title="Edit source"
                    data-testid="edit-book-source-btn"
                  >
                    <Edit size={14} />
                  </button>
                </div>
              ) : recipe.custom_source ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <span>{/^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.(com|org|net|io|co|edu|gov|me|app|dev|cooking|recipes))/.test(recipe.custom_source) ? '🔗' : '👨‍👩‍👧'}</span>
                  <span>Source:</span>
                  {/* Check if custom_source looks like a URL and make it clickable */}
                  {/^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-zA-Z]{2,})/.test(recipe.custom_source) ? (
                    <a 
                      href={recipe.custom_source.startsWith('http') ? recipe.custom_source : `https://${recipe.custom_source}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:text-amber-700 hover:underline"
                    >
                      {recipe.custom_source}
                    </a>
                  ) : (
                    <span className="text-gray-700">{recipe.custom_source}</span>
                  )}
                  {/* Edit button - only show for recipe creator */}
                  <button
                    onClick={() => {
                      setCustomSource(recipe.custom_source);
                      // Detect if it's a URL or personal attribution
                      const isUrl = /^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.(com|org|net|io|co|edu|gov|me|app|dev|cooking|recipes))/.test(recipe.custom_source);
                      setSourceType(isUrl ? 'url' : 'personal');
                      setEditingSource(true);
                    }}
                    className="ml-1 p-1 text-gray-400 hover:text-amber-600 transition-colors"
                    title="Edit source"
                    data-testid="edit-source-btn"
                  >
                    <Edit size={14} />
                  </button>
                </div>
              ) : recipe.source_owner_name && (recipe.original_source_recipe_id || recipe.source_recipe_id) ? (
                <div className="flex items-start gap-3 text-sm text-gray-500 mb-2">
                  {/* Source Image thumbnail - displayed inline with attribution */}
                  {recipe.source_image && (
                    <img 
                      src={recipe.source_image.startsWith('data:') ? recipe.source_image : 
                           recipe.source_image.startsWith('http') ? recipe.source_image :
                           recipe.source_image.startsWith('/9j/') ? `data:image/jpeg;base64,${recipe.source_image}` :
                           recipe.source_image.startsWith('iVBOR') ? `data:image/png;base64,${recipe.source_image}` :
                           `data:image/jpeg;base64,${recipe.source_image}`}
                      alt={`From ${recipe.source_owner_name}`}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-2">
                      <span>{computedTweaks.length > 0 ? '✨' : '🐝'}</span>
                      <span>{computedTweaks.length > 0 ? 'Inspired by:' : 'From:'}</span>
                      <a 
                        href={`/shared/${recipe.original_source_recipe_id || recipe.source_recipe_id}`}
                        className="text-amber-600 hover:text-amber-700 hover:underline font-medium"
                      >
                        {recipe.source_owner_name}
                      </a>
                      <span className="text-gray-400">on Honey</span>
                      {/* Add/Edit source button */}
                      <button
                        onClick={() => {
                          const src = recipe.custom_source || '';
                          setCustomSource(src);
                          // Detect if it's a URL or personal attribution
                          if (recipe.book_source) {
                            setSourceType('book');
                            setBookSource(recipe.book_source);
                          } else if (src && /^(https?:\/\/|www\.|[a-zA-Z0-9-]+\.(com|org|net|io|co|edu|gov|me|app|dev|cooking|recipes))/.test(src)) {
                            setSourceType('url');
                          } else if (src) {
                            setSourceType('personal');
                          } else {
                            setSourceType('url'); // Default for new source
                          }
                          setEditingSource(true);
                        }}
                        className="ml-1 p-1 text-gray-400 hover:text-amber-600 transition-colors"
                        title="Add original source"
                        data-testid="add-source-btn"
                      >
                        <Edit size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* Show computed tweaks (live diff) if available, otherwise fall back to stored modifications */}
              {(computedTweaks.length > 0 || (recipe.modifications && recipe.modifications.length > 0)) && (
                <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <div className="text-amber-700 mb-1 flex items-center gap-1 font-medium">
                    <span>🔧</span>
                    <span>Your tweaks:</span>
                  </div>
                  <ul className="text-amber-800 space-y-0.5 pl-5">
                    {(computedTweaks.length > 0 ? computedTweaks : recipe.modifications).map((mod, idx) => (
                      <li key={idx} className="text-xs">• {mod}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Source Image - from original recipe source (Paprika imports, etc.) - only show if NOT already displayed inline with source_url or source_owner_name */}
              {recipe.source_image && !recipe.source_owner_name && !recipe.source_url && (
                <div className="mt-3">
                  <img 
                    src={recipe.source_image.startsWith('data:') ? recipe.source_image : 
                         recipe.source_image.startsWith('http') ? recipe.source_image :
                         recipe.source_image.startsWith('/9j/') ? `data:image/jpeg;base64,${recipe.source_image}` :
                         recipe.source_image.startsWith('iVBOR') ? `data:image/png;base64,${recipe.source_image}` :
                         `data:image/jpeg;base64,${recipe.source_image}`}
                    alt={`${recipe.title} from source`}
                    className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Editable Source - show for recipes without any source attribution OR when editing existing source */}
          {((!recipe.source_url && !recipe.source_owner_name && !recipe.custom_source && !recipe.book_source) || editingSource) && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {editingSource ? (
                <div className="space-y-3">
                  {/* Source Type Radio Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={sourceType === 'url'}
                        onChange={() => setSourceType('url')}
                        className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm">🔗 Website</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={sourceType === 'book'}
                        onChange={() => setSourceType('book')}
                        className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm">📚 Cookbook</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="sourceType"
                        checked={sourceType === 'personal'}
                        onChange={() => setSourceType('personal')}
                        className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm">👨‍👩‍👧 Personal</span>
                    </label>
                  </div>
                  
                  {/* URL Input */}
                  {sourceType === 'url' && (
                    <input
                      type="text"
                      value={customSource}
                      onChange={(e) => setCustomSource(e.target.value)}
                      placeholder="e.g., allrecipes.com/recipe/..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-600"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveCustomSource();
                        if (e.key === 'Escape') {
                          setEditingSource(false);
                          setCustomSource(recipe.custom_source || '');
                        }
                      }}
                      data-testid="custom-source-input"
                    />
                  )}
                  
                  {/* Book Source Fields */}
                  {sourceType === 'book' && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={bookSource.title}
                        onChange={(e) => setBookSource(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Book title *"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-600"
                        autoFocus
                        data-testid="book-title-input"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={bookSource.author}
                          onChange={(e) => setBookSource(prev => ({ ...prev, author: e.target.value }))}
                          placeholder="Author"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-600"
                          data-testid="book-author-input"
                        />
                        <input
                          type="text"
                          value={bookSource.page}
                          onChange={(e) => setBookSource(prev => ({ ...prev, page: e.target.value }))}
                          placeholder="Page #"
                          className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-600"
                          data-testid="book-page-input"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Personal Attribution Input */}
                  {sourceType === 'personal' && (
                    <input
                      type="text"
                      value={customSource}
                      onChange={(e) => setCustomSource(e.target.value)}
                      placeholder="e.g., Grandma Rose, Mom's recipe, Family tradition..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-600"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveCustomSource();
                        if (e.key === 'Escape') {
                          setEditingSource(false);
                          setCustomSource(recipe.custom_source || '');
                        }
                      }}
                      data-testid="personal-source-input"
                    />
                  )}
                  
                  {/* Save/Cancel/Remove Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={handleSaveCustomSource}
                      disabled={(sourceType === 'url' && !customSource.trim()) || (sourceType === 'book' && !bookSource.title.trim()) || (sourceType === 'personal' && !customSource.trim()) || savingSource}
                      className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-source-btn"
                    >
                      {savingSource ? 'Saving...' : 'Save Source'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingSource(false);
                        setCustomSource(recipe.custom_source || '');
                        if (recipe.book_source) {
                          setSourceType('book');
                          setBookSource(recipe.book_source);
                        } else {
                          setSourceType('url');
                          setBookSource({ title: '', author: '', page: '' });
                        }
                      }}
                      className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                    {/* Remove source button - only show if there's an existing source */}
                    {(recipe.custom_source || recipe.book_source) && (
                      <button
                        onClick={async () => {
                          setSavingSource(true);
                          try {
                            await recipeApi.update(recipeId, { custom_source: null, book_source: null });
                            setRecipe(prev => ({ ...prev, custom_source: null, book_source: null }));
                            setEditingSource(false);
                            setCustomSource('');
                            setBookSource({ title: '', author: '', page: '' });
                            toast.success('Source removed');
                          } catch (error) {
                            toast.error('Failed to remove source');
                          } finally {
                            setSavingSource(false);
                          }
                        }}
                        disabled={savingSource}
                        className="px-4 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        data-testid="remove-source-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setEditingSource(true)}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-amber-600 transition-colors"
                  data-testid="add-source-btn"
                >
                  <Plus size={14} />
                  <span>Add source attribution</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Desktop: Side-by-side layout | Mobile: Full-screen tabs */}
        {isMobile ? (
          /* MOBILE LAYOUT: Full-screen tabs that stick to top when scrolled */
          <>
            {/* Mobile Tab Toggle - sticky, sticks below header when scrolled */}
            <div ref={tabBarRef} className="bg-white border-b border-gray-200 z-30 sticky shadow-sm" style={{ top: headerHeight }}>
              <div className="flex">
                <button
                  onClick={() => handleTabSwitch('ingredients')}
                  data-testid="ingredients-tab-mobile"
                  className={`flex-1 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                    activeTab === 'ingredients'
                      ? 'text-amber-600 bg-amber-50 border-b-2 border-amber-600'
                      : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                  }`}
                >
                  INGREDIENTS
                </button>
                <button
                  onClick={() => handleTabSwitch('instructions')}
                  data-testid="instructions-tab-mobile"
                  className={`flex-1 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                    activeTab === 'instructions'
                      ? 'text-amber-600 bg-amber-50 border-b-2 border-amber-600'
                      : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                  }`}
                >
                  PREPARATION
                </button>
              </div>
            </div>
            
            <div 
              ref={mainContentRef}
              className="bg-white flex-1 pb-20"
            >
              <div className="p-5">
                {activeTab === 'ingredients' && (
                  <div className="space-y-4">
                    {scale !== 1 && (
                      <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                        📐 Showing {scale === 0.5 ? 'half' : 'double'} quantities
                      </div>
                    )}
                    {recipe.ingredients && recipe.ingredients.length > 0 ? (() => {
                      let currentCategory = null;
                      // Aisle names should NOT be displayed as section headers
                      const aisleNames = ['produce', 'dairy', 'meat', 'bakery', 'pantry', 'condiments', 'spices', 'frozen', 'beverages', 'staples', 'other'];
                      return (recipe.ingredients || []).map((ing, idx) => {
                        // Only show header if it's a real section (not an aisle name)
                        const isRealSection = ing.category && !aisleNames.includes(ing.category.toLowerCase());
                        const showHeader = isRealSection && ing.category !== currentCategory;
                        currentCategory = isRealSection ? ing.category : currentCategory;
                        const scaledQuantity = scaleQuantity(ing.quantity, scale);
                        // Only show quantity/unit if they exist and aren't null/empty
                        const hasQuantity = scaledQuantity && scaledQuantity !== 'null' && scaledQuantity.trim() !== '';
                        const hasUnit = ing.unit && ing.unit !== 'null' && ing.unit.trim() !== '';
                        return (
                          <React.Fragment key={idx}>
                            {showHeader && (
                              <h4 className="font-bold text-gray-800 mt-6 mb-3 pt-3 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0 text-base">
                                {ing.category}
                              </h4>
                            )}
                            <label className="flex items-start gap-4 cursor-pointer py-1">
                              <div 
                                onClick={() => toggleIngredient(idx)}
                                className={`mt-1.5 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                                  checkedIngredients.has(idx) ? 'bg-amber-600 border-amber-600' : 'border-gray-300 hover:border-amber-400'
                                }`}
                              >
                                {checkedIngredients.has(idx) && (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                )}
                              </div>
                              <span className={`text-lg leading-relaxed ${checkedIngredients.has(idx) ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                {(hasQuantity || hasUnit) && (
                                  <span className="font-semibold">{hasQuantity ? scaledQuantity : ''}{hasQuantity && hasUnit ? ' ' : ''}{hasUnit ? ing.unit : ''} </span>
                                )}
                                {ing.name}
                                {ing.preparation && <span className="text-gray-500"> ({ing.preparation})</span>}
                              </span>
                            </label>
                          </React.Fragment>
                        );
                      });
                    })() : (
                      !fullDataLoaded ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                          <span className="ml-2 text-gray-500">Loading ingredients...</span>
                        </div>
                      ) : <p className="text-gray-500">No ingredients available.</p>
                    )}
                    
                    {/* Nutrition Section */}
                    {recipe.nutrition && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <h4 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Nutrition (per serving)</h4>
                        {/* Serving size info */}
                        {(recipe.servings || recipe.serving_size) && (
                          <p className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">Serving Size:</span> {recipe.serving_size || (recipe.servings ? `1 serving (makes ${scale !== 1 ? Math.round(recipe.servings * scale) : recipe.servings} servings)` : 'Not specified')}
                          </p>
                        )}
                        <div className="grid grid-cols-3 gap-3">
                          {recipe.nutrition.calories && (
                            <div className="bg-amber-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-amber-700">{recipe.nutrition.calories}</div>
                              <div className="text-xs text-gray-600">Calories</div>
                            </div>
                          )}
                          {recipe.nutrition.protein && (
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-gray-700">{recipe.nutrition.protein}</div>
                              <div className="text-xs text-gray-600">Protein</div>
                            </div>
                          )}
                          {recipe.nutrition.carbohydrates && (
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-gray-700">{recipe.nutrition.carbohydrates}</div>
                              <div className="text-xs text-gray-600">Carbs</div>
                            </div>
                          )}
                          {recipe.nutrition.fat && (
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-gray-700">{recipe.nutrition.fat}</div>
                              <div className="text-xs text-gray-600">Fat</div>
                            </div>
                          )}
                          {recipe.nutrition.fiber && (
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-gray-700">{recipe.nutrition.fiber}</div>
                              <div className="text-xs text-gray-600">Fiber</div>
                            </div>
                          )}
                          {recipe.nutrition.sodium && (
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <div className="text-lg font-bold text-gray-700">{recipe.nutrition.sodium}</div>
                              <div className="text-xs text-gray-600">Sodium</div>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-3 italic" data-testid="nutrition-disclaimer-mobile">
                          {nutritionPending ? (
                            <>Nutrition is being recalculated to reflect your recent changes.</>
                          ) : (recipe.nutrition_source === 'scraped' || (!recipe.nutrition_source && recipe.source_url)) ? (
                            <>Nutrition data from {recipe.source_url ? <a href={recipe.source_url} target="_blank" rel="noopener noreferrer" className="underline">source website</a> : 'source website'}. Values may vary by preparation.</>
                          ) : (recipe.nutrition_source === 'paprika_import' || (!recipe.nutrition_source && recipe.imported_from === 'paprika')) ? (
                            <>Nutrition imported from Paprika. Values not independently verified.</>
                          ) : (
                            <>Nutritional values are AI-estimated based on listed ingredients. For verified data, consult <a href="https://fdc.nal.usda.gov" target="_blank" rel="noopener noreferrer" className="underline">USDA FoodData Central</a>.</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'instructions' && (
                  <div className="space-y-6">
                    {recipe.instructions && recipe.instructions.length > 0 ? (() => {
                      let currentSection = null;
                      return (recipe.instructions || []).map((inst, idx) => {
                        const showHeader = inst.section && inst.section !== currentSection;
                        currentSection = inst.section || currentSection;
                        return (
                          <React.Fragment key={idx}>
                            {showHeader && (
                              <h4 className="font-bold text-amber-700 text-lg mt-6 mb-4 pt-4 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0">
                                {inst.section}
                              </h4>
                            )}
                            <div className="flex gap-4">
                              <div className="flex-shrink-0 w-10 flex flex-col items-center pt-1">
                                {inst.duration_minutes ? (
                                  canLaunchNativeTimer() ? (
                                    <button
                                      onClick={() => launchNativeTimer(inst.duration_minutes, `Step ${inst.step_number}: ${inst.text.substring(0, 50)}`)}
                                      className="flex flex-col items-center hover:text-amber-600 transition-colors cursor-pointer"
                                      title={`Set ${inst.duration_minutes} min timer`}
                                      data-testid={`timer-btn-${idx}`}
                                    >
                                      <Timer size={16} className="text-amber-600" />
                                      <span className="text-xs text-amber-600 mt-0.5 underline">{inst.duration_minutes}m</span>
                                    </button>
                                  ) : (
                                    <>
                                      <Timer size={16} className="text-gray-600" />
                                      <span className="text-xs text-gray-600 mt-0.5">{inst.duration_minutes}m</span>
                                    </>
                                  )
                                ) : <div className="h-5" />}
                              </div>
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center font-bold text-amber-600 text-sm">
                                  {inst.step_number}
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="text-gray-700 leading-relaxed text-base">
                                  {highlightIngredients(inst.text, recipe.ingredients)}
                                </p>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      });
                    })() : (
                      !fullDataLoaded ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                          <span className="ml-2 text-gray-500">Loading...</span>
                        </div>
                      ) : <p className="text-gray-500">No preparation steps available.</p>
                    )}
                    
                    {recipe.recipe_tips && recipe.recipe_tips.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="font-semibold text-gray-900 mb-3">Tips & Tricks</h3>
                        <ul className="space-y-2 list-disc pl-5">
                          {(recipe.recipe_tips || []).map((tip, idx) => (
                            <li key={idx} className="text-gray-700 leading-relaxed">{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* DESKTOP LAYOUT: Side-by-side grid - wider like NYT */
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="grid grid-cols-10 gap-0 divide-x divide-gray-200">
              {/* Ingredients Column - 4/10 width (~40%) */}
              <div className="col-span-4 p-6 lg:p-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-900 uppercase tracking-wide">
                  Ingredients
                </h2>
                {scale !== 1 && (
                  <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mb-4">
                    📐 Showing {scale === 0.5 ? 'half' : 'double'} quantities
                  </div>
                )}
                <div className="space-y-3">
                  {(() => {
                    let currentCategory = null;
                    // Aisle names should NOT be displayed as section headers
                    const aisleNames = ['produce', 'dairy', 'meat', 'bakery', 'pantry', 'condiments', 'spices', 'frozen', 'beverages', 'staples', 'other'];
                    return (recipe.ingredients || []).map((ing, idx) => {
                      // Only show header if it's a real section (not an aisle name)
                      const isRealSection = ing.category && !aisleNames.includes(ing.category.toLowerCase());
                      const showHeader = isRealSection && ing.category !== currentCategory;
                      currentCategory = isRealSection ? ing.category : currentCategory;
                      const scaledQuantity = scaleQuantity(ing.quantity, scale);
                      // Only show quantity/unit if they exist and aren't null/empty
                      const hasQuantity = scaledQuantity && scaledQuantity !== 'null' && scaledQuantity.trim() !== '';
                      const hasUnit = ing.unit && ing.unit !== 'null' && ing.unit.trim() !== '';
                      return (
                        <React.Fragment key={idx}>
                          {showHeader && (
                            <h4 className="font-bold text-gray-800 mt-5 mb-2 pt-3 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0 uppercase text-sm tracking-wide">
                              {ing.category}
                            </h4>
                          )}
                          <label className="flex items-start gap-3 cursor-pointer group">
                            <div 
                              onClick={() => toggleIngredient(idx)}
                              data-testid={`ingredient-checkbox-${idx}`}
                              className={`mt-1 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                                checkedIngredients.has(idx) ? 'bg-amber-600 border-amber-600' : 'border-gray-300 hover:border-amber-400'
                              }`}
                            >
                              {checkedIngredients.has(idx) && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              )}
                            </div>
                            <span className={`${checkedIngredients.has(idx) ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                              {(hasQuantity || hasUnit) && (
                                <span className="font-semibold">{hasQuantity ? scaledQuantity : ''}{hasQuantity && hasUnit ? ' ' : ''}{hasUnit ? ing.unit : ''} </span>
                              )}
                              {ing.name}
                              {ing.preparation && <span className="text-gray-500"> ({ing.preparation})</span>}
                            </span>
                          </label>
                        </React.Fragment>
                      );
                    });
                  })()}
                </div>
                
                {/* Nutrition Section - Desktop */}
                {recipe.nutrition && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Nutrition (per serving)</h3>
                    {/* Serving size info */}
                    {(recipe.servings || recipe.serving_size) && (
                      <p className="text-sm text-gray-600 mb-3">
                        <span className="font-medium">Serving Size:</span> {recipe.serving_size || (recipe.servings ? `1 serving (makes ${scale !== 1 ? Math.round(recipe.servings * scale) : recipe.servings} servings)` : 'Not specified')}
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      {recipe.nutrition.calories && (
                        <div className="bg-amber-50 rounded-lg p-2 text-center">
                          <div className="text-base font-bold text-amber-700">{recipe.nutrition.calories}</div>
                          <div className="text-xs text-gray-600">Calories</div>
                        </div>
                      )}
                      {recipe.nutrition.protein && (
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <div className="text-base font-bold text-gray-700">{recipe.nutrition.protein}</div>
                          <div className="text-xs text-gray-600">Protein</div>
                        </div>
                      )}
                      {recipe.nutrition.carbohydrates && (
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <div className="text-base font-bold text-gray-700">{recipe.nutrition.carbohydrates}</div>
                          <div className="text-xs text-gray-600">Carbs</div>
                        </div>
                      )}
                      {recipe.nutrition.fat && (
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <div className="text-base font-bold text-gray-700">{recipe.nutrition.fat}</div>
                          <div className="text-xs text-gray-600">Fat</div>
                        </div>
                      )}
                      {recipe.nutrition.fiber && (
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <div className="text-base font-bold text-gray-700">{recipe.nutrition.fiber}</div>
                          <div className="text-xs text-gray-600">Fiber</div>
                        </div>
                      )}
                      {recipe.nutrition.sodium && (
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <div className="text-base font-bold text-gray-700">{recipe.nutrition.sodium}</div>
                          <div className="text-xs text-gray-600">Sodium</div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-3 italic" data-testid="nutrition-disclaimer-desktop">
                      {nutritionPending ? (
                        <>Nutrition is being recalculated to reflect your recent changes.</>
                      ) : (recipe.nutrition_source === 'scraped' || (!recipe.nutrition_source && recipe.source_url)) ? (
                        <>Nutrition data from {recipe.source_url ? <a href={recipe.source_url} target="_blank" rel="noopener noreferrer" className="underline">source website</a> : 'source website'}. Values may vary by preparation.</>
                      ) : (recipe.nutrition_source === 'paprika_import' || (!recipe.nutrition_source && recipe.imported_from === 'paprika')) ? (
                        <>Nutrition imported from Paprika. Values not independently verified.</>
                      ) : (
                        <>Nutritional values are AI-estimated based on listed ingredients. For verified data, consult <a href="https://fdc.nal.usda.gov" target="_blank" rel="noopener noreferrer" className="underline">USDA FoodData Central</a>.</>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Instructions Column - 6/10 width (~60%) */}
              <div className="col-span-6 p-6 lg:p-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-900 uppercase tracking-wide">
                  Preparation
                </h2>
                <div className="space-y-5">
                  {recipe.instructions && recipe.instructions.length > 0 ? (() => {
                    let currentSection = null;
                    return (recipe.instructions || []).map((inst, idx) => {
                      const showHeader = inst.section && inst.section !== currentSection;
                      currentSection = inst.section || currentSection;
                      return (
                        <React.Fragment key={idx}>
                          {showHeader && (
                            <h4 className="font-bold text-gray-800 mt-6 mb-3 pt-4 border-t border-gray-200 first:border-t-0 first:mt-0 first:pt-0 uppercase text-sm tracking-wide">
                              {inst.section}
                            </h4>
                          )}
                          <div className="flex gap-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center font-bold text-amber-600">
                              {inst.step_number}
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-700 leading-relaxed">
                                {highlightIngredients(inst.text, recipe.ingredients)}
                              </p>
                              {inst.duration_minutes && (
                                canLaunchNativeTimer() ? (
                                  <button
                                    onClick={() => launchNativeTimer(inst.duration_minutes, `Step ${inst.step_number}: ${inst.text.substring(0, 50)}`)}
                                    className="flex items-center gap-1 mt-2 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
                                    title={`Set ${inst.duration_minutes} min timer`}
                                  >
                                    <Timer size={14} />
                                    <span className="text-xs underline">{inst.duration_minutes} min</span>
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-1 mt-2 text-gray-600">
                                    <Timer size={14} />
                                    <span className="text-xs">{inst.duration_minutes} min</span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    });
                  })() : (
                    !fullDataLoaded ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                        <span className="ml-2 text-gray-500">Loading...</span>
                      </div>
                    ) : <p className="text-gray-500">No preparation steps available.</p>
                  )}
                </div>
                
                {recipe.recipe_tips && recipe.recipe_tips.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3 uppercase text-sm tracking-wide">Tips & Tricks</h3>
                    <ul className="space-y-2 list-disc pl-5">
                      {recipe.recipe_tips.map((tip, idx) => (
                        <li key={idx} className="text-gray-700 leading-relaxed">{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Floating Chat Button with Tip */}
      {!showChat && (
        <div className="fixed bottom-24 right-6 z-40">
          {/* Cook-Along Tip Tooltip */}
          {showCookAlongTip && (
            <div className="absolute bottom-16 right-0 w-64 bg-white rounded-lg shadow-xl border border-amber-200 p-3 animate-fade-in">
              <button 
                onClick={() => setShowCookAlongTip(false)}
                className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
              <div className="flex items-start gap-2">
                <span className="text-xl">✨</span>
                <div>
                  <p className="text-sm font-medium text-gray-800 mb-1">Refine this recipe here!</p>
                  <p className="text-xs text-gray-600">Use the Cook-Along Assistant to make changes, ask about substitutions, or get cooking tips—all updates stay with this recipe.</p>
                </div>
              </div>
              <div className="absolute -bottom-2 right-6 w-3 h-3 bg-white border-r border-b border-amber-200 transform rotate-45"></div>
            </div>
          )}
          <button
            onClick={() => {
              setShowChat(true);
              setShowCookAlongTip(false);
            }}
            data-testid="open-chat-button"
            className={`p-4 bg-amber-600 text-white rounded-full shadow-lg hover:bg-amber-700 transition-all hover:scale-110 ${showCookAlongTip ? 'animate-pulse ring-4 ring-amber-200' : ''}`}
          >
            <MessageSquare size={24} />
          </button>
        </div>
      )}

      {/* Draggable Chat Window */}
      {showChat && (
        <DraggableChat
          recipe={recipe}
          onClose={() => setShowChat(false)}
          onRecipeUpdate={handleRecipeUpdate}
          onShowUpdatePreview={handleShowUpdatePreview}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditRecipeModal
          recipe={recipe}
          onClose={() => setShowEditModal(false)}
          onSave={handleRecipeUpdate}
        />
      )}

      {/* Print Options Modal */}
      {showPrintModal && (
        <PrintModal
          recipe={recipe}
          scale={scale}
          scaleQuantity={scaleQuantity}
          onClose={() => setShowPrintModal(false)}
        />
      )}

      {/* Share to Buzz Modal */}
      {showBuzzModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center pb-16 sm:pb-0">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[75vh] sm:max-h-[85vh] overflow-hidden shadow-xl animate-fade-in flex flex-col">
            {/* Fixed header with recipe title */}
            <div className="bg-gradient-to-r from-amber-400 to-amber-600 p-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white min-w-0">
                  <span className="text-xl flex-shrink-0">🐝</span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold">Honey, what&apos;s your buzz?</h3>
                    <p className="text-xs text-white/90 truncate">{recipe?.title}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowBuzzModal(false);
                    setBuzzCaption('');
                    setBuzzPhoto(null);
                    setBuzzRating(0);
                    setSelectedHives([]);
                    setShowInMyBuzz(true);
                  }}
                  className="text-white/80 hover:text-white flex-shrink-0"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Scrollable content with sticky "Share to" section */}
            <div className="overflow-y-auto flex-1 min-h-0">
              {/* Photo, Caption, Rating - scrolls away */}
              <div className="p-4 pb-2 space-y-3">
                {/* Photo section */}
                <div>
                  <input
                    type="file"
                    id="buzz-photo-input"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target.result.split(',')[1];
                          setBuzzPhoto(base64);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <div 
                    onClick={() => document.getElementById('buzz-photo-input')?.click()}
                    className="h-28 bg-gray-100 rounded-lg overflow-hidden cursor-pointer relative group"
                  >
                    {buzzPhoto ? (
                      <>
                        <img 
                          src={`data:image/jpeg;base64,${buzzPhoto}`}
                          alt="Your dish"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-sm font-medium flex items-center gap-2">
                            <Camera size={18} />
                            Change Photo
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center gap-3 text-gray-400 hover:text-amber-600 transition-colors">
                        <Camera size={24} />
                        <div>
                          <span className="text-sm font-medium">Add a Photo</span>
                          <span className="text-xs ml-1">(optional)</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Caption input */}
                <textarea
                  value={buzzCaption}
                  onChange={(e) => setBuzzCaption(e.target.value)}
                  placeholder="Add a caption... How did it turn out? 🍽️"
                  className="w-full p-2 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  rows={2}
                />
              
                {/* Star Rating */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Rate:</span>
                  <StarRating rating={buzzRating} onChange={setBuzzRating} />
                  {buzzRating > 0 && (
                    <button 
                      onClick={() => setBuzzRating(0)}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Clear
                    </button>
                  )}
                  <span className="text-xs text-gray-400">(optional)</span>
                </div>
                
                {/* Source Attribution removed for MVP personal cooking journal */}
              </div>
              
              {/* Share To section - only show if user has hives */}
              {!loadingHives && myHives.length > 0 ? (
                <>
                  {/* Sticky "Share to" header */}
                  <div className="sticky top-0 bg-white border-y border-gray-200 px-4 py-2 z-10">
                    <p className="text-sm font-medium text-gray-700">Share to:</p>
                  </div>
                  
                  {/* Hive selection list */}
                  <div className="px-4 pb-4">
                    {/* My Buzz toggle */}
                    <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => setShowInMyBuzz(!showInMyBuzz)}
                    >
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                        showInMyBuzz ? 'bg-amber-600 border-amber-600' : 'border-gray-300'
                      }`}>
                        {showInMyBuzz && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                      </div>
                      <span className="text-xl">🐝</span>
                      <span className="text-sm font-medium">My Buzz</span>
                      <span className="text-xs text-gray-500">(personal cooking journal)</span>
                    </label>
                    
                    {/* Hives */}
                    <div className="space-y-1">
                      {/* Active hives */}
                      {myHives.filter(h => !h.is_dormant).map(hive => (
                        <label 
                          key={hive.hive_id}
                          className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleHiveSelection(hive.hive_id)}
                        >
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                            selectedHives.includes(hive.hive_id) ? 'bg-amber-600 border-amber-600' : 'border-gray-300'
                          }`}>
                            {selectedHives.includes(hive.hive_id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                          </div>
                          <span className="text-xl">{hive.icon}</span>
                          <span className="text-sm font-medium">{hive.name}</span>
                          {hive.hive_type === 'private' ? (
                            <span className="text-xs text-gray-400">🔒 (hive only)</span>
                          ) : (
                            <span className="text-xs text-gray-500">(hive & recipe)</span>
                          )}
                        </label>
                      ))}
                      
                      {/* Dormant hives section */}
                      {myHives.filter(h => h.is_dormant).length > 0 && (
                        <div className="border-t pt-2 mt-2">
                          <p className="text-xs text-gray-400 mb-1 px-2 flex items-center gap-1">
                            <span>💤</span> Dormant Hives <span className="text-gray-300">(buzzing will wake them up!)</span>
                          </p>
                          {myHives.filter(h => h.is_dormant).map(hive => (
                            <label 
                              key={hive.hive_id}
                              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                              onClick={() => toggleHiveSelection(hive.hive_id)}
                            >
                              <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${
                                selectedHives.includes(hive.hive_id) ? 'bg-amber-600 border-amber-600' : 'border-gray-300'
                              }`}>
                                {selectedHives.includes(hive.hive_id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                              </div>
                              <span className="text-xl opacity-50">{hive.icon}</span>
                              <span className="text-sm font-medium text-gray-500">{hive.name}</span>
                              {hive.hive_type === 'private' ? (
                                <span className="text-xs text-gray-400">🔒</span>
                              ) : null}
                            </label>
                          ))}
                        </div>
                      )}
                      
                      {/* Find more hives option */}
                      <button 
                        onClick={() => { 
                          setShowBuzzModal(false); 
                          navigate('/feed?tab=discover', { 
                            state: { 
                              returnToBuzz: true, 
                              buzzRecipeId: recipeId, 
                              buzzRecipeTitle: recipe?.title,
                              // Preserve buzz session state
                              buzzState: {
                                caption: buzzCaption,
                                photo: buzzPhoto,
                                rating: buzzRating,
                                selectedHives: selectedHives,
                                showInMyBuzz: showInMyBuzz,
                                sourceType: buzzSourceType,
                                sourceUrl: buzzSourceUrl,
                                bookSource: buzzBookSource
                              }
                            } 
                          }); 
                        }}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-amber-50 cursor-pointer w-full text-left group"
                      >
                        <span className="w-4 h-4 flex items-center justify-center text-amber-600">+</span>
                        <span className="text-xl">🔍</span>
                        <span className="text-sm font-medium text-amber-600 group-hover:underline">Find more hives</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : !loadingHives ? (
                /* No hives - automatically share to My Buzz (personal cooking journal) */
                <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                    <span className="text-xl">📔</span>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Saving to your personal cooking journal</p>
                      <p className="text-xs text-amber-600">Your buzz will be saved to My Buzz</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="px-4 pb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-2 ml-2">
                    <Loader size={14} className="animate-spin" />
                    Loading...
                  </div>
                </div>
              )}
            </div>
            
            {/* Actions - fixed at bottom */}
            <div className="flex gap-3 p-4 border-t bg-gray-50 flex-shrink-0">
              <button
                onClick={() => {
                  setShowBuzzModal(false);
                  setBuzzCaption('');
                  setBuzzPhoto(null);
                  setSelectedHives([]);
                  setShowInMyBuzz(true);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleShareToBuzz}
                disabled={sharingToBuzz || (!showInMyBuzz && selectedHives.length === 0)}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium flex items-center justify-center gap-2 disabled:bg-gray-300 text-sm"
              >
                {sharingToBuzz ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

// Wrap with error boundary
const RecipeDetailWithErrorBoundary = (props) => (
  <RecipeDetailErrorBoundary>
    <RecipeDetail {...props} />
  </RecipeDetailErrorBoundary>
);

export default RecipeDetailWithErrorBoundary;